import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Card, Space, Typography, Select, DatePicker, Tag, Button, Row, Col, message, Modal, Input, InputNumber, Checkbox, Divider, Alert, Popconfirm, Table, Spin, Steps } from 'antd';
import { PlusOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { ApartmentOutlined, TeamOutlined, TrophyOutlined } from '@ant-design/icons';
import PageHeader from './PageHeader';
import campaignService from '../services/campaignService';
import businessUnitService from '../services/businessUnitService';
import settingsService from '../services/settingsService';
import reportService from '../services/reportService';
import { resolveDepartmentGroup } from '../constants/departmentGroups';
import MultiCampaignReport from './MultiCampaignReport';
import BusinessUnitReport from './BusinessUnitReport';
import NewReportPage from './new-report/NewReportPage';
import NewBUReportPage from './new-report/NewBUReportPage';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const DEFAULT_MULTI_CAMPAIGN_REPORT_NAME = 'Multi-Campaign Analysis Report';

const roundTo = (value, digits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Number(numeric.toFixed(digits));
};

const ensureChartHasData = (chart = {}, placeholder = 'No Data', formatPercentDisplayFn = (value) => `${value}%`) => {
  const labels = Array.isArray(chart.labels) ? chart.labels : [];
  if (labels.length > 0) {
    return chart;
  }

  const datasets = (chart.datasets || []).map(dataset => {
    const hasDataLabels = Array.isArray(dataset.dataLabels);
    return {
      ...dataset,
      data: [0],
      dataLabels: hasDataLabels ? [formatPercentDisplayFn(0, 2)] : dataset.dataLabels
    };
  });

  return {
    labels: [placeholder],
    datasets
  };
};

const Reports = () => {
  const [selectedReportType, setSelectedReportType] = useState(null); // null | 'multi-campaign' | 'business-unit' | 'department'
  const [reportName, setReportName] = useState(DEFAULT_MULTI_CAMPAIGN_REPORT_NAME);

  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameReportId, setRenameReportId] = useState(null);
  const [renameTitle, setRenameTitle] = useState('');

  const loadSavedReports = useCallback(async () => {
    setSavedReportsLoading(true);
    const result = await reportService.getSavedReports();
    setSavedReports(result.data || []);
    setSavedReportsLoading(false);
  }, []);

  useEffect(() => { loadSavedReports(); }, [loadSavedReports]);

  const openSavedReport = (id) => {
    window.open(`/report-view?id=${id}`, '_blank');
  };

  const deleteSavedReport = async (id) => {
    const result = await reportService.deleteSavedReport(id);
    if (result.success) {
      setSavedReports(prev => prev.filter(r => r._id !== id));
      message.success('Report deleted');
    } else {
      message.error('Failed to delete report');
    }
  };

  const handleRenameReport = async () => {
    if (!renameTitle.trim() || !renameReportId) return;
    const result = await reportService.updateSavedReport(renameReportId, { title: renameTitle.trim() });
    if (result.success) {
      setSavedReports(prev => prev.map(r => r._id === renameReportId ? { ...r, title: renameTitle.trim() } : r));
      message.success('Report renamed');
    } else {
      message.error('Failed to rename report');
    }
    setRenameModalOpen(false);
  };

  const renderSavedReports = () => {
    const TYPE_LABELS = { 'multi-campaign': 'Multi-Campaign', 'business-unit': 'Business Unit', 'department': 'Department', 'single': 'Dealership' };
    const TYPE_COLORS = { 'multi-campaign': '#267a96', 'business-unit': '#52c41a', 'department': '#fa8c16', 'single': '#722ed1' };

    return (
      <>
        <Card
          title={<Space><FileTextOutlined style={{ color: '#267a96' }} /><span>Recent Reports</span></Space>}
          style={{ marginBottom: 24 }}
          extra={<Button size="small" onClick={loadSavedReports} loading={savedReportsLoading}>Refresh</Button>}
        >
          {savedReportsLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
          ) : savedReports.length === 0 ? (
            <Text type="secondary">No reports yet — generate one and it will appear here automatically.</Text>
          ) : (
            <div>
              {savedReports.slice(0, 10).map((report, idx) => {
                const hasConfig = !!(report.config && Object.keys(report.config).length);
                const reportMonthDayjs = report.config?.month ? dayjs(report.config.month) : null;
                const nextMonthLabel = reportMonthDayjs ? reportMonthDayjs.add(1, 'month').format('MMM YYYY') : null;
                const isFromPriorMonth = reportMonthDayjs ? reportMonthDayjs.isBefore(dayjs(), 'month') : false;

                return (
                  <div
                    key={report._id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0',
                      borderBottom: idx < Math.min(savedReports.length, 10) - 1 ? '1px solid #f0f0f0' : 'none',
                      gap: 12,
                    }}
                  >
                    {/* Left: meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <Tag
                          style={{
                            background: TYPE_COLORS[report.reportType || 'multi-campaign'] + '18',
                            color: TYPE_COLORS[report.reportType || 'multi-campaign'],
                            border: `1px solid ${TYPE_COLORS[report.reportType || 'multi-campaign']}40`,
                            fontSize: 10, fontWeight: 700, margin: 0, padding: '0 6px',
                          }}
                        >
                          {TYPE_LABELS[report.reportType || 'multi-campaign']}
                        </Tag>
                        <Text strong style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {report.title}
                        </Text>
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {[report.managingGroup, report.reportMonth, report.createdAt && new Date(report.createdAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })].filter(Boolean).join(' · ')}
                      </Text>
                    </div>

                    {/* Right: actions */}
                    <Space size={6} style={{ flexShrink: 0 }}>
                      <Button
                        size="small"
                        onClick={() => openSavedReport(report._id)}
                      >
                        Open
                      </Button>
                      {hasConfig && (
                        <Button
                          size="small"
                          type="primary"
                          style={{ background: '#267a96', borderColor: '#267a96' }}
                          onClick={() => loadReportConfigIntoBuilder(report, false)}
                        >
                          ↻ Regenerate
                        </Button>
                      )}
                      {hasConfig && isFromPriorMonth && nextMonthLabel && (
                        <Button
                          size="small"
                          style={{ borderColor: '#267a96', color: '#267a96' }}
                          onClick={() => loadReportConfigIntoBuilder(report, true)}
                        >
                          ↻ For {nextMonthLabel}
                        </Button>
                      )}
                      <Button size="small" type="text" onClick={() => { setRenameReportId(report._id); setRenameTitle(report.title); setRenameModalOpen(true); }}>✎</Button>
                      <Popconfirm title="Delete this report?" onConfirm={() => deleteSavedReport(report._id)} okText="Delete" okType="danger" cancelText="Cancel">
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Rename modal */}
        <Modal
          title="Rename Report"
          open={renameModalOpen}
          onOk={handleRenameReport}
          onCancel={() => setRenameModalOpen(false)}
          okText="Save"
        >
          <Input
            value={renameTitle}
            onChange={e => setRenameTitle(e.target.value)}
            onPressEnter={handleRenameReport}
            placeholder="Report name"
          />
        </Modal>
      </>
    );
  };
  const [mcSelectedMonth, setMcSelectedMonth] = useState(null); // Multi-campaign month selection
  const [mcAllManagingGroups, setMcAllManagingGroups] = useState([]); // Available managing groups
  const [mcSelectedManagingGroup, setMcSelectedManagingGroup] = useState([]); // Selected managing groups
  const [mcAllGroupOwners, setMcAllGroupOwners] = useState([]); // { name, managingGroups[] } from Settings
  const [mcSelectedGroupOwner, setMcSelectedGroupOwner] = useState(null); // Filter MGs by owner
  const [mcAllOrganizations, setMcAllOrganizations] = useState([]); // All organizations with managingGroup info
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [backendReportLoading, setBackendReportLoading] = useState(false); // For backend report generation
  const [mcSaving, setMcSaving] = useState(false);
  const mcCampaignsRequestId = useRef(0);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [departmentGroups, setDepartmentGroups] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [showGroupingConfig, setShowGroupingConfig] = useState(false);
  const [mcWizardStep, setMcWizardStep] = useState(0);
  const [buWizardStep, setBuWizardStep] = useState(0);
  const [editableGroups, setEditableGroups] = useState([]);
  const [mcRestoreGroups, setMcRestoreGroups] = useState(null); // groups to restore after next Load Campaigns
  const [mcLoadedFromReport, setMcLoadedFromReport] = useState(null); // { title } when form pre-filled from saved report
  const [customGroupModalOpen, setCustomGroupModalOpen] = useState(false);
  const [customGroupName, setCustomGroupName] = useState('');
  const [customGroupDepts, setCustomGroupDepts] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [availableDeptPresets, setAvailableDeptPresets] = useState([]);
  const [savePresetModalOpen, setSavePresetModalOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [loadedPresetName, setLoadedPresetName] = useState(null);

  // PDF Options
  const [mcPdfOptions, setMcPdfOptions] = useState({
    networkName: '',
    reportLabel: 'National Performance Report',
    orgLabel: 'Dealership',
    orgLabelPlural: 'Dealerships',
    deptLabel: 'Department',
    deptLabelPlural: 'Departments',
  });
  const [mcPdfProfiles, setMcPdfProfiles] = useState([]);

  // Load PDF profiles on mount
  useEffect(() => {
    settingsService.getPdfProfiles().then(result => {
      setMcPdfProfiles(Array.isArray(result.data) ? result.data : []);
    });
  }, []);

  // Load presets when entering the multi-campaign interface
  useEffect(() => {
    if (selectedReportType === 'multi-campaign') {
      const loadPresets = async () => {
        const result = await settingsService.getDepartmentGroupPresets();
        if (result.success) {
          const presets = result.data?.data || result.data || [];
          setAvailableDeptPresets(Array.isArray(presets) ? presets : []);
        } else {
          console.error('Failed to load dept group presets:', result.message);
        }
      };
      loadPresets();
    }
  }, [selectedReportType]);

  // Auto-generate report name using managing group and month selections
  useEffect(() => {
    let generatedName = DEFAULT_MULTI_CAMPAIGN_REPORT_NAME;
    if (mcSelectedManagingGroup.length > 0) {
      generatedName += ` - ${mcSelectedManagingGroup.join(', ')}`;
    }
    if (mcSelectedMonth) {
      generatedName += ` - ${mcSelectedMonth.format('MMMM YYYY')}`;
    }
    setReportName(generatedName);
  }, [mcSelectedManagingGroup, mcSelectedMonth]);


  // Business Unit Report State
  const [buAllOrganizations, setBuAllOrganizations] = useState([]);
  const [buSelectedOrganization, setBuSelectedOrganization] = useState(null);
  const [buSelectedMonth, setBuSelectedMonth] = useState(null);
  const [buCampaigns, setBuCampaigns] = useState([]);
  const [buSelectedCampaignIds, setBuSelectedCampaignIds] = useState([]);
  const [buReportData, setBuReportData] = useState(null);
  const [buReportLoading, setBuReportLoading] = useState(false);
  const [buReportName, setBuReportName] = useState('Business Unit Analysis Report');
  const [buCompletionRateBenchmark, setBuCompletionRateBenchmark] = useState(70);
  const [buScoreBenchmark, setBuScoreBenchmark] = useState(70);

  // Helper functions
  const formatPercentDisplay = useCallback((value, digits = 0) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0%';
    const maxDigits = Math.min(digits, 2);
    return `${numeric.toFixed(maxDigits)}%`;
  }, []);

  const formatNumberDisplay = useCallback((value, digits = 0) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0';
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }, []);

  const percentValue = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.round(numeric * 100) / 100;
  }, []);

  // Load managing groups and organizations for multi-campaign report
  const loadMcManagingGroupsAndOrgs = useCallback(async () => {
    try {
      // Load managing groups from settings
      const settingsResult = await settingsService.getSettings();
      console.log('Settings result:', settingsResult);
      // settingsResult.data contains the settings object with managingGroups
      if (settingsResult.success && settingsResult.data) {
        const managingGroups = settingsResult.data.managingGroups || settingsResult.data.data?.managingGroups || [];
        const groupOwners = settingsResult.data.groupOwners || settingsResult.data.data?.groupOwners || [];
        console.log('Managing groups loaded:', managingGroups);
        setMcAllManagingGroups(managingGroups);
        setMcAllGroupOwners(groupOwners);
      }
      
      // Load all organizations with their managing group info
      const orgsResult = await businessUnitService.getAllBusinessUnits();
      console.log('Orgs result:', orgsResult);
      if (orgsResult.success && Array.isArray(orgsResult.data)) {
        setMcAllOrganizations(orgsResult.data.map(bu => ({
          id: bu._id,
          name: bu.name,
          managingGroup: bu.managingGroup || ''
        })));
      }
    } catch (error) {
      console.error('Failed to load managing groups:', error);
    }
  }, []);

  // Load campaigns for multi-campaign report (by month and managing group)
  const loadMcCampaigns = useCallback(async () => {
    if (!mcSelectedMonth || !mcSelectedManagingGroup.length) {
      mcCampaignsRequestId.current += 1; // cancel any in-flight requests
      setCampaigns([]);
      setCampaignsLoading(false);
      return;
    }

    const requestId = ++mcCampaignsRequestId.current;

    try {
      setCampaignsLoading(true);
      
      // Get start and end of selected month (for request)
      const monthStartDayjs = mcSelectedMonth.startOf('month');
      const selectedYear = monthStartDayjs.year();
      const selectedMonthIndex = monthStartDayjs.month();
      const monthEndDayjs = mcSelectedMonth.endOf('month');
      const monthStart = monthStartDayjs.toISOString();
      const monthEnd = monthEndDayjs.toISOString();

      console.log('Loading campaigns for:', { monthStart, monthEnd, mcSelectedManagingGroup });

      const params = { 
        limit: 200, 
        status: 'all',
        startDate: monthStart,
        endDate: monthEnd
      };
      
      const res = await campaignService.getAll(params);
      console.log('Campaigns API response:', res);
      
      if (res.success && Array.isArray(res.data)) {
        console.log('Total campaigns in month:', res.data.length);
        
        // Get organization names that belong to the selected managing groups
        const orgsInGroup = mcAllOrganizations
          .filter(o => mcSelectedManagingGroup.includes(o.managingGroup))
          .map(o => o.name.toLowerCase());
        
        console.log('Orgs in selected managing group:', orgsInGroup);
        
        // Filter campaigns that target organizations in the selected managing group AND started in the selected month
        const filteredCampaigns = res.data.filter(campaign => {
          const campaignStart = campaign.startDate ? dayjs(campaign.startDate) : null;
          if (!campaignStart) return false;
          const startMatchesMonth = campaignStart.year() === selectedYear && campaignStart.month() === selectedMonthIndex;
          if (!startMatchesMonth) {
            return false;
          }
          // Method 1: Check targetPaths.summary.businessUnitNames
          const businessUnitNames = campaign.targetPaths?.summary?.businessUnitNames || [];
          if (businessUnitNames.some(name => orgsInGroup.includes(name?.toLowerCase()))) {
            return true;
          }
          
          // Method 2: Check targetPaths.displayPaths
          const displayPaths = campaign.targetPaths?.displayPaths || [];
          for (const path of displayPaths) {
            const segments = path.split('/').map(s => s.trim()).filter(Boolean);
            if (segments.length >= 2) {
              const orgName = segments[1].toLowerCase();
              if (orgsInGroup.includes(orgName)) {
                return true;
              }
            }
          }
          
          // Method 3: Check targets array
          const targets = campaign.targets || [];
          for (const target of targets) {
            const orgName = (target.organizationName || target.businessUnit || '').toLowerCase();
            if (orgsInGroup.includes(orgName)) {
              return true;
            }
          }
          
          // Method 4: Check targetPaths.summary.managingGroups directly
          const campaignManagingGroups = campaign.targetPaths?.summary?.managingGroups || [];
          if (campaignManagingGroups.some(g => mcSelectedManagingGroup.includes(g))) {
            return true;
          }
          
          console.log(`Campaign "${campaign.name}" - no match found. targetPaths:`, campaign.targetPaths);
          return false;
        });

        console.log('Filtered campaigns:', filteredCampaigns.length);
        
        // Ignore outdated responses
        if (requestId !== mcCampaignsRequestId.current) {
          return;
        }
        
        // Only show campaigns that match the selected managing group
        setCampaigns(filteredCampaigns);
        
        // Auto-select all campaigns by default
        setSelectedCampaignIds(filteredCampaigns.map(c => c._id));
        
        if (filteredCampaigns.length === 0 && res.data.length > 0) {
          console.log('No campaigns found for selected managing groups');
          message.info(`No campaigns found for ${mcSelectedManagingGroup.join(', ')} in selected month`);
        }
      }
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      message.error('Failed to load campaigns');
    } finally {
      if (requestId === mcCampaignsRequestId.current) {
        setCampaignsLoading(false);
      }
    }
  }, [mcSelectedMonth, mcSelectedManagingGroup, mcAllOrganizations]);

  useEffect(() => {
    if (selectedReportType === 'multi-campaign') {
      loadMcManagingGroupsAndOrgs();
    }
  }, [selectedReportType, loadMcManagingGroupsAndOrgs]);

  useEffect(() => {
    if (selectedReportType === 'multi-campaign' && mcSelectedMonth && mcSelectedManagingGroup.length > 0) {
      loadMcCampaigns();
    }
  }, [selectedReportType, mcSelectedMonth, mcSelectedManagingGroup, loadMcCampaigns]);

  const campaignOptions = useMemo(() => {
    return campaigns.map(c => {
      let statusColor = 'default';
      let statusText = c.status || 'Unknown';
      
      if (c.status === 'completed') {
        statusColor = 'success';
        statusText = 'Completed';
      } else if (c.status === 'pending' || c.status === 'draft') {
        statusColor = 'warning';
        statusText = 'Pending';
      } else if (c.status === 'active' || c.status === 'launched') {
        statusColor = 'processing';
        statusText = 'Active';
      }
      
      // Extract organization from targetPaths or displayPaths
      let organization = 'No Organization';
      if (c.targetPaths?.summary?.businessUnitNames && c.targetPaths.summary.businessUnitNames.length > 0) {
        organization = c.targetPaths.summary.businessUnitNames[0];
        if (c.targetPaths.summary.businessUnitNames.length > 1) {
          organization += ` +${c.targetPaths.summary.businessUnitNames.length - 1}`;
        }
      } else if (c.targetPaths?.displayPaths && c.targetPaths.displayPaths.length > 0) {
        const firstPath = c.targetPaths.displayPaths[0];
        const segments = firstPath.split('/').map(s => s.trim()).filter(Boolean);
        if (segments.length >= 2) {
          organization = segments[1]; // Second segment is business unit name
        }
      } else if (c.targets && c.targets.length > 0 && c.targets[0].organizationName) {
        organization = c.targets[0].organizationName;
      }
      
      return {
        label: c.name,
        value: c._id,
        status: statusText,
        statusColor: statusColor,
        organization: organization
      };
    });
  }, [campaigns]);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedCampaignIds.length) {
      message.info('Please select at least one campaign');
      return;
    }

    setReportLoading(true);
    
    try {
      const filterParams = {};
      if (selectedOrganizations.length > 0) {
        filterParams.organizations = selectedOrganizations.join(',');
      }

      const results = await Promise.allSettled(
        selectedCampaignIds.map(async (campaignId) => {
          const [statsResult, questionsResult, targetsResult] = await Promise.allSettled([
            campaignService.getCampaignStatistics(campaignId, filterParams),
            campaignService.getCampaignQuestionAnalysis(campaignId, filterParams),
            campaignService.getCampaignTargets(campaignId, { 
              fields: 'participantId._id,participantId.email',
              limit: 10000,
              ...filterParams
            })
          ]);

          const stats = statsResult.status === 'fulfilled' && statsResult.value?.success
            ? statsResult.value.data
            : null;
          const questions = questionsResult.status === 'fulfilled' && questionsResult.value?.success
            ? questionsResult.value.data
            : null;
          const targets = targetsResult.status === 'fulfilled' && targetsResult.value?.success
            ? targetsResult.value.data?.targets || []
            : [];

          const name = campaigns.find((c) => c._id === campaignId)?.name || 'Campaign';

          return { id: campaignId, name, stats, questions, targets };
        })
      );

      const successful = results
        .filter((res) => res.status === 'fulfilled' && res.value?.stats)
        .map((res) => res.value);

      if (!successful.length) {
        message.error('No campaign stats could be loaded');
        return;
      }

      // Build overview
      const overview = buildAggregatedOverview(successful);
      
      // Prepare organization filter based on managing groups
      let orgsInGroup = null;
      if (mcSelectedManagingGroup.length > 0 && mcAllOrganizations.length > 0) {
        orgsInGroup = mcAllOrganizations
          .filter(o => mcSelectedManagingGroup.includes(o.managingGroup))
          .map(o => o.name.toLowerCase());
        
        // Also include each managing group name itself
        mcSelectedManagingGroup.forEach(g => {
          const gLower = g.toLowerCase();
          if (!orgsInGroup.includes(gLower)) orgsInGroup.push(gLower);
        });
        
        console.log('📊 Filter orgs in managing groups:', orgsInGroup);
      }
      
      // Build organizations (filtered at source if managing group selected)
      const organizations = buildAggregatedOrganizations(successful, orgsInGroup);
      
      // Build departments (filtered at source if managing group selected)
      const departments = buildAggregatedDepartments(successful, orgsInGroup);
      
      console.log('📊 Organizations after filter:', organizations.map(o => o.organization));
      console.log('📊 Departments count after filter:', departments.length);
      
      // Build department groups (auto-grouped) from filtered departments
      const autoGroups = buildAggregatedDepartmentGroups(departments);
      
      // Convert auto groups to editable format
      const editableAutoGroups = autoGroups.map(group => ({
        ...group,
        id: group.key,
        isCustom: false,
        editable: true
      }));
      
      // Extract unique departments for selection
      const uniqueDepts = Array.from(new Set(
        departments.map(dept => dept.department).filter(Boolean)
      ));
      setAvailableDepartments(uniqueDepts.sort());
      
      // Build questions
      const questions = buildAggregatedQuestionInsights(successful);
      
      // Store raw data for later
      setReportData({
        _rawData: {
          overview,
          organizations,
          allDepartments: departments,
          questions,
          campaigns: successful.map((item) => ({ id: item.id, name: item.name }))
        }
      });
      
      // Set editable groups and show grouping config
      setEditableGroups(editableAutoGroups);
      setShowGroupingConfig(true);
      
      message.success('Campaign data loaded - now configure department grouping');
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  }, [selectedCampaignIds, selectedOrganizations, campaigns, mcSelectedManagingGroup, mcAllOrganizations]);

  // Generate report using backend (heavy lifting on server)
  const handleGenerateReportFromBackend = useCallback(async () => {
    if (!selectedCampaignIds.length) {
      message.info('Please select at least one campaign');
      return;
    }

    setBackendReportLoading(true);
    
    try {
      console.log('📊 Generating report from backend...');
      console.log('📊 Campaign IDs:', selectedCampaignIds);
      console.log('📊 Managing Groups:', mcSelectedManagingGroup);
      
      const result = await campaignService.generateMultiCampaignReport(
        selectedCampaignIds,
        mcSelectedManagingGroup
      );
      
      if (!result.success || !result.data) {
        message.error(result.message || 'Failed to generate report from backend');
        return;
      }
      
      const data = result.data;
      console.log('📊 Backend report data received:', data);
      
      // Set available departments
      setAvailableDepartments(data.availableDepartments || []);

      // Restore saved groups if coming from a saved-report config; otherwise use backend defaults
      setEditableGroups(mcRestoreGroups || data.departmentGroups || []);
      setMcRestoreGroups(null);

      // Store raw data for later use
      setReportData({
        _rawData: {
          overview: data.overview,
          organizations: data.organizations,
          allDepartments: data.departments,
          questions: data.questions,
          campaigns: data.campaigns
        }
      });
      
      message.success('Campaigns loaded — configure department groups below');
    } catch (error) {
      console.error('Error generating report from backend:', error);
      message.error('Failed to generate report from backend');
    } finally {
      setBackendReportLoading(false);
    }
  }, [selectedCampaignIds, mcSelectedManagingGroup, mcRestoreGroups]);

  const buildAggregatedOverview = useCallback((items = []) => {
    const aggregate = {
      totalTargets: 0,
      uniqueEmployees: 0,
      completed: 0,
      inProgress: 0,
      pending: 0,
      expired: 0,
      excluded: 0,
      averageScore: 0,
      completionRate: 0,
      responseRate: 0,
      sms: { sent: 0, delivered: 0, failed: 0, pending: 0, deliveryRate: '0%' },
      whatsapp: { sent: 0, success: 0, failure: 0, pending: 0, successRate: '0%' },
      targetDelivery: { attempted: 0, delivered: 0, deliveryRate: '0%' }
    };

    let weightedScoreSum = 0;
    let weightedScoreCount = 0;
    const uniqueParticipantIds = new Set();

    items.forEach((item) => {
      const overview = item?.stats?.overview || {};
      aggregate.totalTargets += overview.total || 0;
      aggregate.completed += overview.completed || 0;
      aggregate.inProgress += overview.inProgress || 0;
      aggregate.pending += overview.pending || 0;
      aggregate.expired += overview.expired || 0;
      aggregate.excluded += overview.excluded || 0;

      // Track unique participants
      const targets = item?.targets || [];
      targets.forEach((target) => {
        const participantId = target?.participantId?._id || target?.participantId?.email;
        if (participantId) {
          uniqueParticipantIds.add(participantId);
        }
      });

      const completedCount = overview.completed || 0;
      const avgScore = Number(overview.averageScore) || 0;
      weightedScoreSum += avgScore * completedCount;
      weightedScoreCount += completedCount;

      aggregate.sms.sent += overview.sms?.sent || 0;
      aggregate.sms.delivered += overview.sms?.delivered || 0;
      aggregate.sms.failed += overview.sms?.failed || 0;
      aggregate.sms.pending += overview.sms?.pending || 0;

      aggregate.whatsapp.sent += overview.whatsapp?.sent || 0;
      aggregate.whatsapp.success += overview.whatsapp?.success || 0;
      aggregate.whatsapp.failure += overview.whatsapp?.failure || 0;
      aggregate.whatsapp.pending += overview.whatsapp?.pending || 0;
      
      // Aggregate target-based delivery (unique targets delivered via ANY channel)
      aggregate.targetDelivery.attempted += overview.targetDelivery?.attempted || 0;
      aggregate.targetDelivery.delivered += overview.targetDelivery?.delivered || 0;
    });

    aggregate.uniqueEmployees = uniqueParticipantIds.size;
    aggregate.averageScore = roundTo(
      weightedScoreCount > 0 ? (weightedScoreSum / weightedScoreCount) : 0
    );
    aggregate.completionRate = roundTo(
      aggregate.totalTargets > 0
        ? (aggregate.completed / aggregate.totalTargets) * 100
        : 0
    );
    aggregate.responseRate = roundTo(
      aggregate.totalTargets > 0
        ? ((aggregate.completed + aggregate.inProgress) / aggregate.totalTargets) * 100
        : 0
    );

    if (aggregate.sms.sent > 0) {
      const smsRate = roundTo((aggregate.sms.delivered / aggregate.sms.sent) * 100);
      aggregate.sms.deliveryRate = `${smsRate.toFixed(2)}%`;
    } else {
      aggregate.sms.deliveryRate = '0%';
    }

    if (aggregate.whatsapp.sent > 0) {
      const whatsappRate = roundTo((aggregate.whatsapp.success / aggregate.whatsapp.sent) * 100);
      aggregate.whatsapp.successRate = `${whatsappRate.toFixed(2)}%`;
    } else {
      aggregate.whatsapp.successRate = '0%';
    }

    // Calculate target-based delivery rate (unique targets delivered via ANY channel)
    // This accounts for WhatsApp→SMS fallback correctly
    if (aggregate.targetDelivery.attempted > 0) {
      const targetDeliveryRate = roundTo((aggregate.targetDelivery.delivered / aggregate.targetDelivery.attempted) * 100);
      aggregate.targetDelivery.deliveryRate = `${targetDeliveryRate.toFixed(2)}%`;
      aggregate.totalChannelSuccessRate = targetDeliveryRate;
      aggregate.totalChannelSuccessRateDisplay = `${targetDeliveryRate.toFixed(2)}%`;
    } else {
      aggregate.targetDelivery.deliveryRate = '0%';
      aggregate.totalChannelSuccessRate = 0;
      aggregate.totalChannelSuccessRateDisplay = '0%';
    }

    return aggregate;
  }, []);

  const buildAggregatedOrganizations = useCallback((items = [], filterOrgs = null) => {
    const orgMap = new Map();

    items.forEach((item) => {
      (item?.stats?.byOrganization || []).forEach((org) => {
        const orgName = org?._id?.organizationName || org?._id || org?.organizationName || 'Organization';
        
        // Skip if filter is set and this org is not in the allowed list
        if (filterOrgs && !filterOrgs.includes(orgName?.toLowerCase())) {
          return;
        }
        const participants = org?.totalParticipants || org?.participants || 0;
        const completed = org?.totalCompleted || org?.completed || 0;

        let avgScore = Number(org?.averageScore);
        let scoreWeight = completed;

        if (!Number.isFinite(avgScore) && Array.isArray(org?.departments)) {
          let sum = 0;
          let comp = 0;
          org.departments.forEach((dept) => {
            const deptCompleted = dept?.completed || dept?.completedCount || 0;
            const deptScore = Number(dept?.averageScore) || 0;
            sum += deptScore * deptCompleted;
            comp += deptCompleted;
          });
          avgScore = comp > 0 ? (sum / comp) : 0;
          scoreWeight = comp;
        }

        const existing = orgMap.get(orgName) || {
          organization: orgName,
          participants: 0,
          completed: 0,
          scoreSum: 0,
          scoreWeight: 0
        };

        existing.participants += participants;
        existing.completed += completed;
        existing.scoreSum += (Number.isFinite(avgScore) ? avgScore : 0) * scoreWeight;
        existing.scoreWeight += scoreWeight;

        orgMap.set(orgName, existing);
      });
    });

    return Array.from(orgMap.values())
      .map((org) => {
        const completionRate = roundTo(org.participants > 0 ? (org.completed / org.participants) * 100 : 0);
        const averageScore = roundTo(org.scoreWeight > 0 ? (org.scoreSum / org.scoreWeight) : 0);
        return {
          ...org,
          completionRate,
          averageScore,
          completionRateDisplay: formatPercentDisplay(completionRate, 2),
          averageScoreDisplay: formatPercentDisplay(averageScore, 2)
        };
      })
      .sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0));
  }, [formatPercentDisplay]);

  const buildAggregatedDepartments = useCallback((items = [], filterOrgs = null) => {
    const deptMap = new Map();

    items.forEach((item) => {
      (item?.stats?.byDepartment || []).forEach((dept) => {
        const name = dept?.department || 'Unassigned';
        const orgName = dept?.organizationName || dept?.organization || dept?.businessUnitName || 'No Organization';
        
        // Skip if filter is set and this org is not in the allowed list (but keep 'No Organization')
        if (filterOrgs && orgName?.toLowerCase() !== 'no organization' && !filterOrgs.includes(orgName?.toLowerCase())) {
          return;
        }
        const mapKey = `${name}|||${orgName}`;
        const participants = dept?.totalParticipants || 0;
        const responded = dept?.respondedCount || 0;
        const completed = dept?.completedCount || 0;
        const averageScore = Number(dept?.averageScore) || 0;

        const existing = deptMap.get(mapKey) || {
          department: name,
          organizationName: orgName,
          totalParticipants: 0,
          respondedCount: 0,
          completedCount: 0,
          scoreSum: 0,
          scoreWeight: 0
        };

        existing.totalParticipants += participants;
        existing.respondedCount += responded;
        existing.completedCount += completed;
        existing.scoreSum += averageScore * completed;
        existing.scoreWeight += completed;

        deptMap.set(mapKey, existing);
      });
    });

    return Array.from(deptMap.values()).map((dept) => {
      const responseRate = roundTo(dept.totalParticipants > 0 ? (dept.respondedCount / dept.totalParticipants) * 100 : 0);
      const completionRate = roundTo(dept.totalParticipants > 0 ? (dept.completedCount / dept.totalParticipants) * 100 : 0);
      const averageScore = roundTo(dept.scoreWeight > 0 ? (dept.scoreSum / dept.scoreWeight) : 0);
      return {
        ...dept,
        responseRate,
        completionRate,
        averageScore,
        responseRateDisplay: formatPercentDisplay(responseRate, 2),
        completionRateDisplay: formatPercentDisplay(completionRate, 2),
        averageScoreDisplay: formatPercentDisplay(averageScore, 2)
      };
    }).sort((a, b) => (b.responseRate || 0) - (a.responseRate || 0));
  }, [formatPercentDisplay]);

  const buildAggregatedDepartmentGroups = useCallback((departments = []) => {
    if (!departments.length) return [];
    const grouped = new Map();

    departments.forEach((dept) => {
      const group = resolveDepartmentGroup(dept?.department);
      const key = group?.key || 'other';
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          label: group?.label || 'Other Departments',
          departments: new Set(),
          totalParticipants: 0,
          respondedCount: 0,
          completedCount: 0,
          scoreSum: 0,
          scoreWeight: 0
        });
      }
      const entry = grouped.get(key);
      if (dept?.department) {
        entry.departments.add(dept.department);
      }
      entry.totalParticipants += dept?.totalParticipants || 0;
      entry.respondedCount += dept?.respondedCount || 0;
      entry.completedCount += dept?.completedCount || 0;
      entry.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
      entry.scoreWeight += dept?.completedCount || 0;
    });

    return Array.from(grouped.values()).map((group) => {
      const responseRate = roundTo(group.totalParticipants > 0 ? (group.respondedCount / group.totalParticipants) * 100 : 0);
      const completionRate = roundTo(group.totalParticipants > 0 ? (group.completedCount / group.totalParticipants) * 100 : 0);
      const averageScore = roundTo(group.scoreWeight > 0 ? (group.scoreSum / group.scoreWeight) : 0);
      return {
        ...group,
        departments: Array.from(group.departments),
        responseRate,
        completionRate,
        averageScore,
        responseRateDisplay: formatPercentDisplay(responseRate, 2),
        completionRateDisplay: formatPercentDisplay(completionRate, 2),
        averageScoreDisplay: formatPercentDisplay(averageScore, 2)
      };
    }).sort((a, b) => (b.completedCount || 0) - (a.completedCount || 0));
  }, [formatPercentDisplay]);

  const buildAggregatedQuestionInsights = useCallback((items = []) => {
    const allQuestions = [];
    const questionsByDeptMap = new Map(); // questionId -> { questionText, byDepartment: { dept -> { correct, total } } }

    items.forEach((item) => {
      const label = item?.name || 'Campaign';
      const q = item?.questions || {};
      
      // Collect overall question stats
      if (q.analysis && Array.isArray(q.analysis)) {
        q.analysis.forEach((question) => {
          if (question && typeof question.failureRate === 'number') {
            allQuestions.push({
              questionText: question.questionText || 'Question',
              failureRate: question.failureRate,
              successRate: question.successRate || (100 - question.failureRate),
              correctAnswers: question.correctAnswers || 0,
              totalAnswers: question.totalAnswers || 0,
              campaign: label
            });
          }
        });
      }
      
      if (q.mostDifficult && typeof q.mostDifficult.failureRate === 'number') {
        allQuestions.push({
          questionText: q.mostDifficult.questionText || 'Question',
          failureRate: q.mostDifficult.failureRate,
          successRate: q.mostDifficult.successRate || (100 - q.mostDifficult.failureRate),
          correctAnswers: q.mostDifficult.correctAnswers || 0,
          totalAnswers: q.mostDifficult.totalAnswers || 0,
          campaign: label
        });
      }
      
      // Collect question stats by department
      if (q.questionsByDepartment && Array.isArray(q.questionsByDepartment)) {
        q.questionsByDepartment.forEach((qItem) => {
          const qId = qItem.questionId;
          const qText = qItem.questionText;
          
          if (!questionsByDeptMap.has(qId)) {
            questionsByDeptMap.set(qId, {
              questionId: qId,
              questionText: qText,
              byDepartment: new Map()
            });
          }
          
          const qEntry = questionsByDeptMap.get(qId);
          
          (qItem.byDepartment || []).forEach((deptData) => {
            const deptName = deptData.department || 'Unassigned';
            if (!qEntry.byDepartment.has(deptName)) {
              qEntry.byDepartment.set(deptName, {
                department: deptName,
                organizationName: deptData.organizationName || 'No Organization',
                totalAnswers: 0,
                correctAnswers: 0
              });
            }
            
            const existing = qEntry.byDepartment.get(deptName);
            existing.totalAnswers += deptData.totalAnswers || 0;
            existing.correctAnswers += deptData.correctAnswers || 0;
          });
        });
      }
    });

    // Convert questionsByDeptMap to array with calculated rates
    const questionsByDepartment = Array.from(questionsByDeptMap.values()).map(q => ({
      questionId: q.questionId,
      questionText: q.questionText,
      byDepartment: Array.from(q.byDepartment.values()).map(dept => {
        const successRate = dept.totalAnswers > 0
          ? roundTo((dept.correctAnswers / dept.totalAnswers) * 100)
          : 0;
        return {
          ...dept,
          successRate,
          failureRate: roundTo(Math.max(0, 100 - successRate))
        };
      })
    }));

    const top10Difficult = allQuestions
      .sort((a, b) => b.failureRate - a.failureRate)
      .slice(0, 10);

    return {
      top10Difficult,
      hardest: top10Difficult[0] || null,
      easiest: allQuestions.length > 0 
        ? allQuestions.sort((a, b) => a.failureRate - b.failureRate)[0]
        : null,
      questionsByDepartment // NEW: questions with department breakdown
    };
  }, []);

  const resolveDepartmentGroup = useCallback((deptName) => {
    if (!deptName || typeof deptName !== 'string') return { key: 'other', label: 'Other Departments' };
    const normalized = deptName.toLowerCase().trim();
    if (/leadership|executive|management|board|ceo|cfo|coo|cto|president|vp|vice president|director/i.test(normalized)) {
      return { key: 'leadership', label: 'Leadership & Executive' };
    }
    if (/sales|marketing|business development|account|customer success/i.test(normalized)) {
      return { key: 'salesMarketing', label: 'Sales & Marketing' };
    }
    if (/operations|logistics|supply chain|warehouse|procurement|facilities/i.test(normalized)) {
      return { key: 'operations', label: 'Operations & Logistics' };
    }
    if (/human resources|hr|people|talent|recruiting|recruitment/i.test(normalized)) {
      return { key: 'hr', label: 'Human Resources' };
    }
    if (/finance|accounting|treasury|audit|payroll/i.test(normalized)) {
      return { key: 'finance', label: 'Finance & Accounting' };
    }
    if (/it|information technology|tech|engineering|software|development|devops|infrastructure/i.test(normalized)) {
      return { key: 'it', label: 'IT & Engineering' };
    }
    if (/legal|compliance|risk|regulatory|counsel/i.test(normalized)) {
      return { key: 'legal', label: 'Legal & Compliance' };
    }
    if (/admin|administration|office|support|reception/i.test(normalized)) {
      return { key: 'admin', label: 'Administration & Support' };
    }
    return { key: 'other', label: 'Other Departments' };
  }, []);

  const reportCards = [
    {
      key: 'multi-campaign',
      title: 'Multi-Campaign Report',
      description: 'Aggregate data from multiple campaigns',
      icon: <ApartmentOutlined style={{ fontSize: 48, color: '#2E86AB' }} />,
    },
    {
      key: 'business-unit',
      title: 'Business Unit Report',
      description: 'Analyze performance by organization',
      icon: <TeamOutlined style={{ fontSize: 48, color: '#2E86AB' }} />,
    },
    {
      key: 'department',
      title: 'Department Report',
      description: 'Compare department performance',
      icon: <TrophyOutlined style={{ fontSize: 48, color: '#2E86AB' }} />,
    },
  ];

  // Handle adding custom group to editable groups
  const handleAddCustomGroup = () => {
    if (!customGroupName.trim()) {
      message.warning('Please enter a group name');
      return;
    }
    if (customGroupDepts.length === 0) {
      message.warning('Please select at least one department for the group');
      return;
    }

    const newGroup = {
      id: `custom-${Date.now()}`,
      name: customGroupName,
      label: customGroupName,
      departments: customGroupDepts,
      isCustom: true,
      editable: true
    };

    setEditableGroups([...editableGroups, newGroup]);
    setCustomGroupName('');
    setCustomGroupDepts([]);
    setCustomGroupModalOpen(false);
    message.success('Custom group added');
  };

  // Handle removing group from editable groups
  const handleRemoveGroup = (groupId) => {
    setEditableGroups(editableGroups.filter(g => g.id !== groupId));
  };

  // Handle updating a group's departments
  const handleUpdateGroupDepartments = (groupId, newDepartments) => {
    setEditableGroups(editableGroups.map(g => 
      g.id === groupId ? { ...g, departments: newDepartments } : g
    ));
  };

  // Recalculate statistics for custom/edited groups
  const recalculateGroupStatistics = useCallback((groups, allDepartments) => {
    return groups.map(group => {
      // Find all departments that belong to this group
      const groupDepts = allDepartments.filter(dept => 
        group.departments?.includes(dept.department)
      );
      
      // Aggregate statistics for departments in this group
      let totalParticipants = 0;
      let respondedCount = 0;
      let completedCount = 0;
      let scoreSum = 0;
      let scoreWeight = 0;
      
      groupDepts.forEach(dept => {
        totalParticipants += dept?.totalParticipants || 0;
        respondedCount += dept?.respondedCount || 0;
        completedCount += dept?.completedCount || 0;
        scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
        scoreWeight += dept?.completedCount || 0;
      });
      
      const responseRate = roundTo(totalParticipants > 0 ? (respondedCount / totalParticipants) * 100 : 0);
      const completionRate = roundTo(totalParticipants > 0 ? (completedCount / totalParticipants) * 100 : 0);
      const averageScore = roundTo(scoreWeight > 0 ? (scoreSum / scoreWeight) : 0);
      
      return {
        ...group,
        totalParticipants,
        respondedCount,
        completedCount,
        responseRate,
        completionRate,
        averageScore,
        responseRateDisplay: formatPercentDisplay(responseRate, 2),
        completionRateDisplay: formatPercentDisplay(completionRate, 2),
        averageScoreDisplay: formatPercentDisplay(averageScore, 2)
      };
    });
  }, [formatPercentDisplay]);

  // Build group-by-organization structure (Group → Org → metrics)
  const buildGroupByOrgStats = useCallback((groups = [], organizations = [], allDepartments = []) => {
    const result = [];
    
    groups.forEach(group => {
      const groupName = group.label || group.name;
      
      // Get all departments in this group
      const groupDepts = allDepartments.filter(dept => 
        group.departments?.includes(dept.department)
      );
      
      // Group departments by organization
      const orgMap = new Map();
      
      groupDepts.forEach(dept => {
        const orgName = dept.organizationName || 'No Organization';
        if (!orgMap.has(orgName)) {
          orgMap.set(orgName, {
            totalParticipants: 0,
            completedCount: 0,
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        
        const orgData = orgMap.get(orgName);
        orgData.totalParticipants += dept?.totalParticipants || 0;
        orgData.completedCount += dept?.completedCount || 0;
        orgData.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
        orgData.scoreWeight += dept?.completedCount || 0;
      });
      
      const labels = [];
      const completionRateData = [];
      const completionRateLabels = [];
      const averageScoreData = [];
      const averageScoreLabels = [];
      
      orgMap.forEach((data, orgName) => {
        const completionRate = roundTo(data.totalParticipants > 0 ? (data.completedCount / data.totalParticipants) * 100 : 0);
        const averageScore = roundTo(data.scoreWeight > 0 ? (data.scoreSum / data.scoreWeight) : 0);
        
        labels.push(orgName);
        completionRateData.push(completionRate);
        completionRateLabels.push(formatPercentDisplay(completionRate, 2));
        averageScoreData.push(averageScore);
        averageScoreLabels.push(formatPercentDisplay(averageScore, 2));
      });
      
      result.push({
        groupName,
        chart: ensureChartHasData({
          labels,
          datasets: [
            {
              label: 'Completion Rate',
              data: completionRateData,
              dataLabels: completionRateLabels
            },
            {
              label: 'Average Score',
              data: averageScoreData,
              dataLabels: averageScoreLabels
            }
          ]
        }, 'No Organizations', formatPercentDisplay)
      });
    });
    
    return result;
  }, [formatPercentDisplay]);

  // Build group-by-department structure (Group → Individual Depts → metrics)
  const buildGroupByDeptStats = useCallback((groups = [], allDepartments = []) => {
    const result = [];
    
    groups.forEach(group => {
      const groupName = group.label || group.name;
      
      // Get all departments in this group
      const groupDepts = allDepartments.filter(dept => 
        group.departments?.includes(dept.department)
      );
      
      // Aggregate by department name (combine across orgs)
      const deptMap = new Map();
      
      groupDepts.forEach(dept => {
        const deptName = dept.department || 'Unassigned';
        if (!deptMap.has(deptName)) {
          deptMap.set(deptName, {
            totalParticipants: 0,
            completedCount: 0,
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        
        const deptData = deptMap.get(deptName);
        deptData.totalParticipants += dept?.totalParticipants || 0;
        deptData.completedCount += dept?.completedCount || 0;
        deptData.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
        deptData.scoreWeight += dept?.completedCount || 0;
      });
      
      const labels = [];
      const completionRateData = [];
      const completionRateLabels = [];
      const averageScoreData = [];
      const averageScoreLabels = [];
      
      deptMap.forEach((data, deptName) => {
        const completionRate = roundTo(data.totalParticipants > 0 ? (data.completedCount / data.totalParticipants) * 100 : 0);
        const averageScore = roundTo(data.scoreWeight > 0 ? (data.scoreSum / data.scoreWeight) : 0);
        
        labels.push(deptName);
        completionRateData.push(completionRate);
        completionRateLabels.push(formatPercentDisplay(completionRate, 2));
        averageScoreData.push(averageScore);
        averageScoreLabels.push(formatPercentDisplay(averageScore, 2));
      });
      
      result.push({
        groupName,
        chart: ensureChartHasData({
          labels,
          datasets: [
            {
              label: 'Completion Rate',
              data: completionRateData,
              dataLabels: completionRateLabels
            },
            {
              label: 'Average Score',
              data: averageScoreData,
              dataLabels: averageScoreLabels
            }
          ]
        }, 'No Departments', formatPercentDisplay)
      });
    });
    
    return result;
  }, [formatPercentDisplay]);

  // Build question success rates by department group (Question → Group → Success Rate)
  const buildQuestionsByDeptGroup = useCallback((questionsByDepartment = [], groups = []) => {
    const result = [];
    
    questionsByDepartment.forEach((question, index) => {
      const labels = [];
      const successRateData = [];
      const successRateLabels = [];
      
      groups.forEach(group => {
        const groupName = group.label || group.name;
        const groupDepts = group.departments || [];
        
        // Find departments in this group that answered this question
        const relevantDepts = (question.byDepartment || []).filter(dept => 
          groupDepts.includes(dept.department)
        );
        
        // Aggregate success rate for this group
        let totalAnswers = 0;
        let correctAnswers = 0;
        
        relevantDepts.forEach(dept => {
          totalAnswers += dept.totalAnswers || 0;
          correctAnswers += dept.correctAnswers || 0;
        });

        if (totalAnswers <= 0) return;
        
        const successRate = roundTo(totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0);
        
        labels.push(groupName);
        successRateData.push(successRate);
        successRateLabels.push(formatPercentDisplay(successRate, 2));
      });
      
      result.push({
        questionNumber: index + 1,
        questionText: question.questionText,
        chart: {
          labels,
          datasets: [
            {
              label: 'Success Rate',
              data: successRateData,
              dataLabels: successRateLabels
            }
          ]
        }
      });
    });
    
    return result;
  }, [formatPercentDisplay]);

  // Build department group question stats (Group → All Questions → Success Rate)
  const buildDeptGroupQuestionStats = useCallback((questionsByDepartment = [], groups = []) => {
    const result = [];
    
    groups.forEach(group => {
      const groupName = group.label || group.name;
      const groupDepts = group.departments || [];
      // Normalise dept names once for efficient case-insensitive matching
      const groupDeptsNorm = groupDepts.map(d => String(d).trim().toLowerCase());
      
      // Deduplicate questions by questionText while aggregating counts
      const questionMap = new Map();
      
      questionsByDepartment.forEach((question) => {
        const qText = question.questionText;
        if (!qText) return;
        
        // Find departments in this group that answered this question (case-insensitive)
        const relevantDepts = (question.byDepartment || []).filter(dept => 
          groupDeptsNorm.includes(String(dept.department || '').trim().toLowerCase())
        );
        
        // Aggregate counts for this question within this group
        let totalAnswers = 0;
        let correctAnswers = 0;
        
        relevantDepts.forEach(dept => {
          totalAnswers += dept.totalAnswers || 0;
          correctAnswers += dept.correctAnswers || 0;
        });

        // Skip questions that no one in this group actually answered
        if (totalAnswers <= 0) return;
        
        // Aggregate into map (deduplicate by questionText)
        if (questionMap.has(qText)) {
          const existing = questionMap.get(qText);
          existing.totalAnswers += totalAnswers;
          existing.correctAnswers += correctAnswers;
        } else {
          questionMap.set(qText, { totalAnswers, correctAnswers });
        }
      });
      
      // Convert map to array with calculated success rates
      const questionsData = [];
      let qNum = 1;
      questionMap.forEach((counts, qText) => {
        const successRate = roundTo(counts.totalAnswers > 0 ? (counts.correctAnswers / counts.totalAnswers) * 100 : 0);
        questionsData.push({
          questionNumber: qNum++,
          questionText: qText,
          successRate,
          successRateDisplay: formatPercentDisplay(successRate, 2)
        });
      });
      
      // Sort by success rate descending then reverse for chart (most difficult first - lowest pass rate)
      questionsData.sort((a, b) => b.successRate - a.successRate);
      
      result.push({
        groupName,
        questions: questionsData,
        chart: {
          labels: questionsData.map(q => q.questionText || `Q${q.questionNumber}`),
          datasets: [
            {
              label: 'Success Rate',
              data: questionsData.map(q => q.successRate),
              dataLabels: questionsData.map(q => q.successRateDisplay)
            }
          ]
        }
      });
    });
    
    return result;
  }, [formatPercentDisplay]);

  // Build unified group analysis structure (Group → byOrg/byDept → completionRate/averageScore charts)
  const buildGroupAnalysis = useCallback((groups = [], allDepartments = [], questionsByDepartment = []) => {
    const result = [];
    
    groups.forEach(group => {
      const groupName = group.label || group.name;
      const groupDepts = group.departments || [];
      
      // Get all departments in this group
      const relevantDepts = allDepartments.filter(dept => 
        groupDepts.includes(dept.department)
      );
      
      // --- BY ORGANIZATION ---
      const orgMap = new Map();
      relevantDepts.forEach(dept => {
        const orgName = dept.organizationName || 'No Organization';
        if (!orgMap.has(orgName)) {
          orgMap.set(orgName, {
            totalParticipants: 0,
            completedCount: 0,
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        const orgData = orgMap.get(orgName);
        orgData.totalParticipants += dept?.totalParticipants || 0;
        orgData.completedCount += dept?.completedCount || 0;
        orgData.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
        orgData.scoreWeight += dept?.completedCount || 0;
      });
      
      // Sort orgs by completion rate
      const orgEntries = Array.from(orgMap.entries()).map(([orgName, data]) => ({
        name: orgName,
        completionRate: roundTo(data.totalParticipants > 0 ? (data.completedCount / data.totalParticipants) * 100 : 0),
        averageScore: roundTo(data.scoreWeight > 0 ? (data.scoreSum / data.scoreWeight) : 0)
      })).sort((a, b) => b.completionRate - a.completionRate);

      const orgEntriesByScore = [...orgEntries].sort((a, b) => b.averageScore - a.averageScore);

      const byOrgCompletionChart = ensureChartHasData({
        labels: orgEntries.map(o => o.name),
        datasets: [{
          label: 'Completion Rate',
          data: orgEntries.map(o => o.completionRate),
          dataLabels: orgEntries.map(o => formatPercentDisplay(o.completionRate, 2))
        }]
      }, 'No Organizations', formatPercentDisplay);

      const byOrgScoreChart = ensureChartHasData({
        labels: orgEntriesByScore.map(o => o.name),
        datasets: [{
          label: 'Average Score',
          data: orgEntriesByScore.map(o => o.averageScore),
          dataLabels: orgEntriesByScore.map(o => formatPercentDisplay(o.averageScore, 2))
        }]
      }, 'No Organizations', formatPercentDisplay);
      
      // --- BY DEPARTMENT ---
      const deptMap = new Map();
      relevantDepts.forEach(dept => {
        const deptName = dept.department || 'Unassigned';
        if (!deptMap.has(deptName)) {
          deptMap.set(deptName, {
            totalParticipants: 0,
            completedCount: 0,
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        const deptData = deptMap.get(deptName);
        deptData.totalParticipants += dept?.totalParticipants || 0;
        deptData.completedCount += dept?.completedCount || 0;
        deptData.scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
        deptData.scoreWeight += dept?.completedCount || 0;
      });
      
      // Create base dept entries
      const deptEntries = Array.from(deptMap.entries()).map(([deptName, data]) => ({
        name: deptName,
        completionRate: roundTo(data.totalParticipants > 0 ? (data.completedCount / data.totalParticipants) * 100 : 0),
        averageScore: roundTo(data.scoreWeight > 0 ? (data.scoreSum / data.scoreWeight) : 0)
      }));
      
      // Sort by completion rate (highest to lowest) for completion chart
      const deptEntriesByCompletion = [...deptEntries].sort((a, b) => b.completionRate - a.completionRate);
      
      const byDeptCompletionChart = ensureChartHasData({
        labels: deptEntriesByCompletion.map(d => d.name),
        datasets: [{
          label: 'Completion Rate',
          data: deptEntriesByCompletion.map(d => d.completionRate),
          dataLabels: deptEntriesByCompletion.map(d => formatPercentDisplay(d.completionRate, 2))
        }]
      }, 'No Departments', formatPercentDisplay);
      
      // Sort by average score (highest to lowest) for score chart
      const deptEntriesByScore = [...deptEntries].sort((a, b) => b.averageScore - a.averageScore);
      
      const byDeptScoreChart = ensureChartHasData({
        labels: deptEntriesByScore.map(d => d.name),
        datasets: [{
          label: 'Average Score',
          data: deptEntriesByScore.map(d => d.averageScore),
          dataLabels: deptEntriesByScore.map(d => formatPercentDisplay(d.averageScore, 2))
        }]
      }, 'No Departments', formatPercentDisplay);
      
      // --- QUESTIONS ANALYSIS ---
      // Deduplicate questions by questionText while aggregating counts
      const questionMap = new Map();
      
      questionsByDepartment.forEach((question) => {
        const qText = question.questionText;
        if (!qText) return;
        
        // Find departments in this group that answered this question
        const relevantQDepts = (question.byDepartment || []).filter(dept => 
          groupDepts.includes(dept.department)
        );
        
        // Aggregate counts for this question within this group
        let totalAnswers = 0;
        let correctAnswers = 0;
        
        relevantQDepts.forEach(dept => {
          totalAnswers += dept.totalAnswers || 0;
          correctAnswers += dept.correctAnswers || 0;
        });
        
        // Aggregate into map (deduplicate by questionText)
        if (questionMap.has(qText)) {
          const existing = questionMap.get(qText);
          existing.totalAnswers += totalAnswers;
          existing.correctAnswers += correctAnswers;
        } else {
          questionMap.set(qText, { totalAnswers, correctAnswers });
        }
      });
      
      // Convert map to array with calculated success rates
      const questionsData = [];
      let qNum = 1;
      questionMap.forEach((counts, qText) => {
        const successRate = roundTo(counts.totalAnswers > 0 ? (counts.correctAnswers / counts.totalAnswers) * 100 : 0);
        questionsData.push({
          questionNumber: qNum++,
          questionText: qText,
          successRate,
          successRateDisplay: formatPercentDisplay(successRate, 2),
          totalAnswers: counts.totalAnswers,
          correctAnswers: counts.correctAnswers
        });
      });
      
      // Sort by success rate descending then reverse for chart (most difficult first)
      questionsData.sort((a, b) => b.successRate - a.successRate);
      
      const questionsChart = ensureChartHasData({
        labels: questionsData.map(q => q.questionText || `Q${q.questionNumber}`),
        datasets: [{
          label: 'Success Rate',
          data: questionsData.map(q => q.successRate),
          dataLabels: questionsData.map(q => q.successRateDisplay)
        }]
      }, 'No Questions', formatPercentDisplay);
      
      result.push({
        groupName,
        byOrganization: {
          completionRate: byOrgCompletionChart,
          averageScore: byOrgScoreChart,
          data: orgEntries
        },
        byDepartment: {
          completionRate: byDeptCompletionChart,
          averageScore: byDeptScoreChart,
          data: deptEntries
        },
        questions: {
          chart: questionsChart,
          data: questionsData
        }
      });
    });
    
    return result;
  }, [formatPercentDisplay]);

  // Build organization × department group cross-tabulation
  const buildOrgByGroupStats = useCallback((organizations = [], groups = [], allDepartments = []) => {
    const crossTab = [];
    
    organizations.forEach(org => {
      groups.forEach(group => {
        // Find departments that belong to this group AND data for this org
        const relevantDepts = allDepartments.filter(dept => 
          group.departments?.includes(dept.department)
        );
        
        // Aggregate only for this organization's departments in this group
        let totalParticipants = 0;
        let completedCount = 0;
        let scoreSum = 0;
        let scoreWeight = 0;
        
        relevantDepts.forEach(dept => {
          // Match organization name
          if (dept.organizationName === org.organization || !dept.organizationName) {
            totalParticipants += dept?.totalParticipants || 0;
            completedCount += dept?.completedCount || 0;
            scoreSum += (dept?.averageScore || 0) * (dept?.completedCount || 0);
            scoreWeight += dept?.completedCount || 0;
          }
        });
        
        // Skip orgs with no staff targeted for this group — they weren't part of this campaign
        if (totalParticipants === 0) return;

        const completionRate = roundTo((completedCount / totalParticipants) * 100);
        const averageScore = roundTo(scoreWeight > 0 ? (scoreSum / scoreWeight) : 0);

        crossTab.push({
          organization: org.organization,
          group: group.label || group.name,
          groupId: group.id,
          totalParticipants,
          completedCount,
          completionRate,
          averageScore,
          completionRateDisplay: formatPercentDisplay(completionRate, 2),
          averageScoreDisplay: formatPercentDisplay(averageScore, 2)
        });
      });
    });

    return crossTab;
  }, [formatPercentDisplay]);

  // Helper: Fetch campaigns for a specific month and managing group (for multi-campaign comparison)
  const fetchMcCampaignsForMonth = useCallback(async (month, managingGroup, allOrgs) => {
    const mgArray = Array.isArray(managingGroup) ? managingGroup : (managingGroup ? [managingGroup] : []);
    if (!month || !mgArray.length) return [];
    
    const monthStartDayjs = month.startOf('month');
    const selectedYear = monthStartDayjs.year();
    const selectedMonthIndex = monthStartDayjs.month();
    const monthEndDayjs = month.endOf('month');
    const monthStart = monthStartDayjs.toISOString();
    const monthEnd = monthEndDayjs.toISOString();
    
    console.log(`Fetching campaigns for ${month.format('MMMM YYYY')}:`, { monthStart, monthEnd, managingGroup });
    
    const params = {
      limit: 200,
      status: 'all',
      startDate: monthStart,
      endDate: monthEnd
    };
    
    const res = await campaignService.getAll(params);
    if (!res.success || !Array.isArray(res.data)) {
      console.log(`No campaigns found for ${month.format('MMMM YYYY')}`);
      return [];
    }
    
    console.log(`Total campaigns in ${month.format('MMMM YYYY')}:`, res.data.length);
    
    const orgsInGroup = allOrgs
      .filter(o => mgArray.includes(o.managingGroup))
      .map(o => o.name.toLowerCase());
    
    console.log('Orgs in managing group:', orgsInGroup);
    
    // Filter campaigns with multiple methods (same as loadMcCampaigns)
    const filteredCampaigns = res.data.filter(campaign => {
      const campaignStart = campaign.startDate ? dayjs(campaign.startDate) : null;
      if (!campaignStart) return false;
      const startMatchesMonth = campaignStart.year() === selectedYear && campaignStart.month() === selectedMonthIndex;
      if (!startMatchesMonth) {
        return false;
      }
      // Method 1: Check targetPaths.summary.businessUnitNames
      const businessUnitNames = campaign.targetPaths?.summary?.businessUnitNames || [];
      if (businessUnitNames.some(name => orgsInGroup.includes(name?.toLowerCase()))) {
        return true;
      }
      
      // Method 2: Check targetPaths.displayPaths
      const displayPaths = campaign.targetPaths?.displayPaths || [];
      for (const path of displayPaths) {
        const segments = path.split('/').map(s => s.trim()).filter(Boolean);
        if (segments.length >= 2) {
          const orgName = segments[1].toLowerCase();
          if (orgsInGroup.includes(orgName)) {
            return true;
          }
        }
      }
      
      // Method 3: Check targets array
      const targets = campaign.targets || [];
      for (const target of targets) {
        const orgName = (target.organizationName || target.businessUnit || '').toLowerCase();
        if (orgsInGroup.includes(orgName)) {
          return true;
        }
      }
      
      // Method 4: Check targetPaths.summary.managingGroups directly
      const campaignManagingGroups = campaign.targetPaths?.summary?.managingGroups || [];
      if (mgArray.some(g => campaignManagingGroups.includes(g))) {
        return true;
      }
      
      return false;
    });
    
    console.log(`Filtered campaigns for ${month.format('MMMM YYYY')}:`, filteredCampaigns.length);
    
    // Only return campaigns that match the selected managing group (no fallback)
    return filteredCampaigns;
  }, []);

  // Helper: Get department group stats from campaigns
  const calculateDeptGroupStatsFromCampaigns = useCallback(async (campaignList, groups) => {
    if (!campaignList.length || !groups.length) return [];
    
    console.log('[calculateDeptGroupStatsFromCampaigns] Processing', campaignList.length, 'campaigns');
    
    // Fetch targets for each campaign (similar to BU Report approach)
    const campaignDataPromises = campaignList.map(async (campaign) => {
      try {
        const targetsResult = await campaignService.getCampaignTargets(campaign._id, { 
          limit: 10000
        });
        
        const targets = targetsResult.success ? targetsResult.data?.targets || [] : [];
        return { campaignId: campaign._id, campaignName: campaign.name, targets };
      } catch (error) {
        console.error('[calculateDeptGroupStatsFromCampaigns] Error fetching targets for campaign', campaign._id, error);
        return { campaignId: campaign._id, campaignName: campaign.name, targets: [] };
      }
    });
    
    const allCampaignData = await Promise.all(campaignDataPromises);
    const totalTargets = allCampaignData.reduce((sum, c) => sum + c.targets.length, 0);
    console.log('[calculateDeptGroupStatsFromCampaigns] Fetched', totalTargets, 'total targets across', allCampaignData.length, 'campaigns');
    
    // Aggregate targets by department
    const deptMap = new Map();
    allCampaignData.forEach(({ targets }) => {
      targets.forEach(target => {
        const participant = target.participantId;
        if (!participant) return;
        
        const dept = participant.department || 'Unknown';
        
        if (!deptMap.has(dept)) {
          deptMap.set(dept, {
            department: dept,
            participantIds: new Set(),
            completedParticipantIds: new Set(),
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        
        const entry = deptMap.get(dept);
        const participantKey = participant.email || participant._id;
        
        if (!participantKey) return;
        
        // Track all unique participants
        entry.participantIds.add(participantKey);
        
        // Track participants who completed at least one campaign
        if (target.status === 'completed') {
          entry.completedParticipantIds.add(participantKey);
          const score = Number(target.score) || 0;
          entry.scoreSum += score;
          entry.scoreWeight += 1;
        }
      });
    });
    
    const allDepartments = Array.from(deptMap.values()).map(dept => ({
      department: dept.department,
      totalParticipants: dept.participantIds.size,
      completedCount: dept.completedParticipantIds.size,
      scoreSum: dept.scoreSum,
      scoreWeight: dept.scoreWeight
    }));
    console.log('[calculateDeptGroupStatsFromCampaigns] Aggregated', allDepartments.length, 'unique departments from targets');
    
    // Calculate stats for each group
    const result = groups.map(group => {
      const groupDepts = allDepartments.filter(d => 
        group.departments?.includes(d.department)
      );
      
      const totalParticipants = groupDepts.reduce((sum, d) => sum + d.totalParticipants, 0);
      const completedCount = groupDepts.reduce((sum, d) => sum + d.completedCount, 0);
      const scoreSum = groupDepts.reduce((sum, d) => sum + d.scoreSum, 0);
      const scoreWeight = groupDepts.reduce((sum, d) => sum + d.scoreWeight, 0);
      
      const completionRate = roundTo(totalParticipants > 0 ? (completedCount / totalParticipants) * 100 : 0);
      const averageScore = roundTo(scoreWeight > 0 ? (scoreSum / scoreWeight) : 0);
      
      console.log('[calculateDeptGroupStatsFromCampaigns] Group', group.label, ':', {
        matchedDepts: groupDepts.length,
        totalParticipants,
        completedCount,
        completionRate,
        averageScore
      });
      
      return {
        groupId: group.id || group.key,
        label: group.label || group.name,
        completionRate,
        averageScore
      };
    });
    
    console.log('[calculateDeptGroupStatsFromCampaigns] Final result:', result);
    return result;
  }, []);

  // Helper: Get organization stats from campaigns
  const calculateOrgStatsFromCampaigns = useCallback(async (campaignList, organizationsList) => {
    if (!campaignList.length || !organizationsList.length) return [];
    
    // Fetch targets for each campaign
    const campaignDataPromises = campaignList.map(async (campaign) => {
      try {
        const targetsResult = await campaignService.getCampaignTargets(campaign._id, { 
          limit: 10000
        });
        
        const targets = targetsResult.success ? targetsResult.data?.targets || [] : [];
        return { campaignId: campaign._id, targets };
      } catch {
        return { campaignId: campaign._id, targets: [] };
      }
    });
    
    const allCampaignData = await Promise.all(campaignDataPromises);
    
    // Aggregate by organization
    const orgMap = new Map();
    allCampaignData.forEach(({ targets }) => {
      targets.forEach(target => {
        const participant = target.participantId;
        if (!participant) return;
        
        const org = participant.organization || 'Unknown';
        
        if (!orgMap.has(org)) {
          orgMap.set(org, {
            organization: org,
            participantIds: new Set(),
            completedParticipantIds: new Set(),
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        
        const entry = orgMap.get(org);
        const participantKey = participant.email || participant._id;
        
        if (!participantKey) return;
        
        // Track all unique participants
        entry.participantIds.add(participantKey);
        
        // Track participants who completed at least one campaign
        if (target.status === 'completed') {
          entry.completedParticipantIds.add(participantKey);
          const score = Number(target.score) || 0;
          entry.scoreSum += score;
          entry.scoreWeight += 1;
        }
      });
    });
    
    // Calculate stats for each organization
    return organizationsList.map(org => {
      const data = orgMap.get(org.organization);
      if (!data) {
        return {
          organization: org.organization,
          completionRate: 0,
          averageScore: 0
        };
      }
      
      const totalParticipants = data.participantIds.size;
      const completedCount = data.completedParticipantIds.size;
      
      const completionRate = roundTo(totalParticipants > 0 ? (completedCount / totalParticipants) * 100 : 0);
      const averageScore = roundTo(data.scoreWeight > 0 ? (data.scoreSum / data.scoreWeight) : 0);
      
      return {
        organization: org.organization,
        completionRate,
        averageScore
      };
    });
  }, []);

  // Helper: Get department stats from campaigns (completion rate per department)
  const calculateDeptStatsFromCampaigns = useCallback(async (campaignList, departmentsList) => {
    if (!campaignList.length) return [];
    
    // Fetch targets for each campaign
    const campaignDataPromises = campaignList.map(async (campaign) => {
      try {
        const targetsResult = await campaignService.getCampaignTargets(campaign._id, { 
          limit: 10000
        });
        
        const targets = targetsResult.success ? targetsResult.data?.targets || [] : [];
        return { campaignId: campaign._id, targets };
      } catch {
        return { campaignId: campaign._id, targets: [] };
      }
    });
    
    const allCampaignData = await Promise.all(campaignDataPromises);
    
    // Aggregate by department
    const deptMap = new Map();
    allCampaignData.forEach(({ targets }) => {
      targets.forEach(target => {
        const participant = target.participantId;
        if (!participant) return;
        
        const dept = participant.department || 'Unknown';
        
        if (!deptMap.has(dept)) {
          deptMap.set(dept, {
            department: dept,
            participantIds: new Set(),
            completedParticipantIds: new Set(),
            scoreSum: 0,
            scoreWeight: 0
          });
        }
        
        const entry = deptMap.get(dept);
        const participantKey = participant.email || participant._id;
        
        if (!participantKey) return;
        
        // Track all unique participants
        entry.participantIds.add(participantKey);
        
        // Track participants who completed at least one campaign
        if (target.status === 'completed') {
          entry.completedParticipantIds.add(participantKey);
          const score = Number(target.score) || 0;
          entry.scoreSum += score;
          entry.scoreWeight += 1;
        }
      });
    });
    
    // Get unique departments from input list or from collected data
    const deptNames = departmentsList.length > 0 
      ? departmentsList.map(d => d.department)
      : Array.from(deptMap.keys());
    
    // Calculate stats for each department
    return deptNames.map(deptName => {
      const data = deptMap.get(deptName);
      if (!data) {
        return {
          department: deptName,
          totalParticipants: 0,
          completedCount: 0,
          completionRate: 0,
          averageScore: 0
        };
      }
      
      const totalParticipants = data.participantIds.size;
      const completedCount = data.completedParticipantIds.size;
      
      const completionRate = roundTo(totalParticipants > 0 ? (completedCount / totalParticipants) * 100 : 0);
      const averageScore = roundTo(data.scoreWeight > 0 ? (data.scoreSum / data.scoreWeight) : 0);
      
      return {
        department: deptName,
        totalParticipants,
        completedCount,
        completionRate,
        averageScore
      };
    });
  }, []);

  // Handle final report generation with configured groups
  const handleGenerateFinalReport = async () => {
    if (!reportData?._rawData) {
      message.error('No report data available');
      return;
    }

    setReportLoading(true);
    try {
      const { overview, organizations, allDepartments, questions, campaigns } = reportData._rawData;
      
      // Data is already filtered by managing group in handleGenerateReport
      // Just use the data as-is
      const filteredOrganizations = organizations;
      const filteredDepartments = allDepartments;
      
      console.log('📊 Final report - organizations:', filteredOrganizations.map(o => o.organization));
      console.log('📊 Final report - departments count:', filteredDepartments.length);
      
      // Recalculate statistics for the edited groups
      const groupsWithStats = recalculateGroupStatistics(editableGroups, filteredDepartments);
      
      // Build group → organization structure
      const groupByOrgStats = buildGroupByOrgStats(groupsWithStats, filteredOrganizations, filteredDepartments);
      console.log('🔍 buildGroupByOrgStats result:', groupByOrgStats);
      console.log('🔍 Input - groupsWithStats:', groupsWithStats);
      console.log('🔍 Input - filteredOrganizations:', filteredOrganizations);
      console.log('🔍 Input - filteredDepartments:', filteredDepartments);
      
      // Build group → individual departments structure
      const groupByDeptStats = buildGroupByDeptStats(groupsWithStats, filteredDepartments);
      
      // Build organization × group cross-tabulation (legacy)
      const orgByGroupStats = buildOrgByGroupStats(filteredOrganizations, groupsWithStats, filteredDepartments);
      
      // Build unified group analysis (byOrg + byDept + questions with separate charts)
      const groupAnalysis = buildGroupAnalysis(groupsWithStats, filteredDepartments, questions.questionsByDepartment || []);
      
      // Build question success rates by department group
      const questionsByDeptGroup = buildQuestionsByDeptGroup(questions.questionsByDepartment || [], groupsWithStats);
      
      // Build department group question stats (all questions per group)
      const deptGroupQuestionStats = buildDeptGroupQuestionStats(questions.questionsByDepartment || [], groupsWithStats);
      
      // Calculate 3-month comparison data (if month selected)
      let departmentGroupComparison = null;
      let organizationComparison = null;
      let departmentComparison = null;
      
      if (mcSelectedMonth && mcSelectedManagingGroup) {
        const month3 = mcSelectedMonth.clone().subtract(3, 'month'); // 3 months ago
        const month1 = mcSelectedMonth.clone().subtract(1, 'month'); // Previous month
        const month2 = mcSelectedMonth.clone().subtract(2, 'month'); // 2 months ago
        
        console.log('=== 3-Month Comparison Debug ===');
        console.log('Selected month:', mcSelectedMonth.format('MMMM YYYY'));
        console.log('Month 3 (3 months ago):', month3.format('MMMM YYYY'));
        console.log('Month 1 (previous):', month1.format('MMMM YYYY'));
        console.log('Month 2 (2 months ago):', month2.format('MMMM YYYY'));
        console.log('Managing Group:', mcSelectedManagingGroup);
        console.log('All Orgs count:', mcAllOrganizations.length);
        
        // Fetch campaigns for previous 2 months
        const [month1Campaigns, month2Campaigns, month3Campaigns] = await Promise.all([
          fetchMcCampaignsForMonth(month1, mcSelectedManagingGroup, mcAllOrganizations),
          fetchMcCampaignsForMonth(month2, mcSelectedManagingGroup, mcAllOrganizations),
          fetchMcCampaignsForMonth(month3, mcSelectedManagingGroup, mcAllOrganizations)
        ]);
        
        console.log('Month 1 campaigns count:', month1Campaigns.length);
        console.log('Month 2 campaigns count:', month2Campaigns.length);
        console.log('Month 3 campaigns count:', month3Campaigns.length);
        
        // Calculate department group stats for each month
        const [month1DeptGroupStats, month2DeptGroupStats, month3DeptGroupStats] = await Promise.all([
          calculateDeptGroupStatsFromCampaigns(month1Campaigns, groupsWithStats),
          calculateDeptGroupStatsFromCampaigns(month2Campaigns, groupsWithStats),
          calculateDeptGroupStatsFromCampaigns(month3Campaigns, groupsWithStats)
        ]);
        
        console.log('Month 1 dept group stats:', month1DeptGroupStats);
        console.log('Month 2 dept group stats:', month2DeptGroupStats);
        console.log('Month 3 dept group stats:', month3DeptGroupStats);
        
        // Calculate organization stats for each month (use filtered organizations)
        const [month1OrgStats, month2OrgStats, month3OrgStats] = await Promise.all([
          calculateOrgStatsFromCampaigns(month1Campaigns, filteredOrganizations),
          calculateOrgStatsFromCampaigns(month2Campaigns, filteredOrganizations),
          calculateOrgStatsFromCampaigns(month3Campaigns, filteredOrganizations)
        ]);
        
        console.log('Month 1 org stats:', month1OrgStats);
        console.log('Month 2 org stats:', month2OrgStats);
        console.log('Month 3 org stats:', month3OrgStats);
        
        // Calculate department stats for each month (use filtered departments)
        const [month1DeptStats, month2DeptStats, month3DeptStats] = await Promise.all([
          calculateDeptStatsFromCampaigns(month1Campaigns, filteredDepartments),
          calculateDeptStatsFromCampaigns(month2Campaigns, filteredDepartments),
          calculateDeptStatsFromCampaigns(month3Campaigns, filteredDepartments)
        ]);
        
        console.log('Month 1 dept stats:', month1DeptStats);
        console.log('Month 2 dept stats:', month2DeptStats);
        console.log('Month 3 dept stats:', month3DeptStats);
        
        // Build comparison data structure for department groups
        departmentGroupComparison = {
          month3: {
            label: month3.format('MMMM'),
            stats: month3DeptGroupStats
          },
          month2: {
            label: month2.format('MMMM'),
            stats: month2DeptGroupStats
          },
          month1: {
            label: month1.format('MMMM'),
            stats: month1DeptGroupStats
          },
          currentMonth: {
            label: mcSelectedMonth.format('MMMM'),
            stats: groupsWithStats.map(g => ({
              groupId: g.id || g.key,
              label: g.label || g.name,
              completionRate: g.completionRate,
              averageScore: g.averageScore
            }))
          }
        };
        
        // Build comparison data structure for organizations (use filtered)
        organizationComparison = {
          month3: {
            label: month3.format('MMMM'),
            stats: month3OrgStats
          },
          month2: {
            label: month2.format('MMMM'),
            stats: month2OrgStats
          },
          month1: {
            label: month1.format('MMMM'),
            stats: month1OrgStats
          },
          currentMonth: {
            label: mcSelectedMonth.format('MMMM'),
            stats: filteredOrganizations.map(o => ({
              organization: o.organization,
              completionRate: o.completionRate,
              averageScore: o.averageScore
            }))
          }
        };
        
        // Build comparison data structure for departments (use filtered)
        departmentComparison = {
          month3: {
            label: month3.format('MMMM'),
            stats: month3DeptStats
          },
          month2: {
            label: month2.format('MMMM'),
            stats: month2DeptStats
          },
          month1: {
            label: month1.format('MMMM'),
            stats: month1DeptStats
          },
          currentMonth: {
            label: mcSelectedMonth.format('MMMM'),
            stats: filteredDepartments.map(d => ({
              department: d.department,
              totalParticipants: d.totalParticipants || 0,
              completedCount: d.completedCount || 0,
              completionRate: d.completionRate,
              averageScore: d.averageScore
            }))
          }
        };
      }
      
      // Build final report data with configured groups (use filtered data)
      const finalReportData = {
        overview: {
          ...(overview || {}),
          reportMonth: mcSelectedMonth ? mcSelectedMonth.format('MMMM YYYY') : (overview?.reportMonth || ''),
        },
        organizations: filteredOrganizations,
        departments: filteredDepartments,
        departmentGroups: groupsWithStats,
        departmentGroupComparison, // 3-month comparison data for dept groups
        organizationComparison, // 3-month comparison data for organizations
        departmentComparison, // 3-month comparison data for departments (completion rate)
        groupByOrgStats,
        groupByDeptStats,
        orgByGroupStats,
        groupAnalysis,
        questionsByDeptGroup,
        deptGroupQuestionStats,
        questions,
        campaigns,
        departmentGroupConfig: {
          groups: groupsWithStats
        }
      };

      // Save to sessionStorage and open in new tab
      const _networkName = mcPdfOptions.networkName || mcSelectedGroupOwner || (mcSelectedManagingGroup.length === 1 ? mcSelectedManagingGroup[0] : '');
      sessionStorage.setItem('mcReportData', JSON.stringify(finalReportData));
      sessionStorage.setItem('mcReportName', reportName || 'Multi-Campaign Report');
      sessionStorage.setItem('mcReportCustomisation', JSON.stringify({
        networkName: _networkName,
        reportLabel: mcPdfOptions.reportLabel,
        orgLabel: mcPdfOptions.orgLabel,
        orgLabelPlural: mcPdfOptions.orgLabelPlural,
        deptLabel: mcPdfOptions.deptLabel,
        deptLabelPlural: mcPdfOptions.deptLabelPlural,
      }));
      sessionStorage.removeItem('mcSavedReportId');
      sessionStorage.removeItem('mcSavedAiInsights');
      setReportData(finalReportData);
      setShowGroupingConfig(false);
      setMcWizardStep(2);

      const configSnapshot = {
        campaignIds: selectedCampaignIds,
        managingGroups: mcSelectedManagingGroup,
        groupOwner: mcSelectedGroupOwner,
        month: mcSelectedMonth?.toISOString(),
        editableGroups,
        pdfOptions: mcPdfOptions,
        reportName,
      };

      try {
        localStorage.setItem('mcLastConfig', JSON.stringify({
          reportName,
          month: mcSelectedMonth?.format('MMMM YYYY'),
          managingGroups: mcSelectedManagingGroup,
          groupOwner: mcSelectedGroupOwner,
          selectedCampaignIds,
          loadedPresetName,
          pdfOptions: { orgLabel: mcPdfOptions.orgLabel, orgLabelPlural: mcPdfOptions.orgLabelPlural, deptLabel: mcPdfOptions.deptLabel, deptLabelPlural: mcPdfOptions.deptLabelPlural },
        }));
      } catch { /* quota exceeded */ }

      message.success('Report ready');
      setTimeout(() => {
        document.getElementById('mc-inline-report')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleSaveReport = async (aiInsights) => {
    if (!reportData) { message.error('No report to save'); return; }
    setMcSaving(true);
    try {
      const now = new Date();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
      const titledName = `${reportName || 'Multi-Campaign Report'} — ${ts}`;
      const result = await reportService.saveReport({
        title: titledName,
        managingGroup: mcSelectedManagingGroup.join(', '),
        reportMonth: mcSelectedMonth ? mcSelectedMonth.format('MMMM YYYY') : '',
        reportType: 'multi-campaign',
        config: {
          managingGroups: mcSelectedManagingGroup,
          groupOwner: mcSelectedGroupOwner,
          month: mcSelectedMonth?.toISOString(),
          editableGroups,
          pdfOptions: mcPdfOptions,
          selectedCampaignIds,
        },
        reportData,
        aiInsights: aiInsights || {},
      });
      if (result.success) {
        message.success('Report saved');
        loadSavedReports();
      } else {
        message.error(result.message || 'Failed to save report');
      }
    } catch (e) {
      message.error('Failed to save report');
    } finally {
      setMcSaving(false);
    }
  };

  // ======== BUSINESS UNIT REPORT FUNCTIONS ========

  // Load all business units for selection
  const loadBusinessUnits = useCallback(async () => {
    try {
      const result = await businessUnitService.getAllBusinessUnits();
      if (result.success && Array.isArray(result.data)) {
        setBuAllOrganizations(result.data.map(bu => ({
          id: bu._id,
          name: bu.name,
          departments: bu.departments || [],
          participantCount: bu.participantCount || 0,
          managingGroup: bu.managingGroup || ''
        })));
      }
    } catch (error) {
      console.error('Failed to load business units:', error);
      message.error('Failed to load organizations');
    }
  }, []);

  // Load campaigns for the selected business unit and month
  const loadBuCampaigns = useCallback(async () => {
    if (!buSelectedOrganization || !buSelectedMonth) {
      setBuCampaigns([]);
      setBuSelectedCampaignIds([]);
      return;
    }

    try {
      setCampaignsLoading(true);

      // Get start and end of the selected month
      const monthStart = buSelectedMonth.startOf('month').toISOString();
      const monthEnd = buSelectedMonth.endOf('month').toISOString();

      const params = { 
        limit: 200, 
        status: 'all',
        startDate: monthStart,
        endDate: monthEnd
      };
      
      const res = await campaignService.getAll(params);
      
      if (res.success && Array.isArray(res.data)) {
        // Filter campaigns that targeted the selected business unit
        const selectedOrgName = buAllOrganizations.find(o => o.id === buSelectedOrganization)?.name?.toLowerCase();
        const selectedOrgId = buSelectedOrganization;

        const filteredCampaigns = res.data.filter(campaign => {
          // Method 1: technicalPaths contain the org _id at segment[1]
          // format: "Managing Group/{orgId}/Department"
          const technicalPaths = campaign.targetPaths?.technicalPaths || [];
          if (technicalPaths.some(path => {
            const segments = path.split('/').map(s => s.trim()).filter(Boolean);
            return segments.length >= 2 && segments[1] === selectedOrgId;
          })) {
            return true;
          }

          // Method 2: displayPaths contain the org name at segment[1]
          // format: "Managing Group/Business Unit Name/Department"
          const displayPaths = campaign.targetPaths?.displayPaths || [];
          if (selectedOrgName && displayPaths.some(path => {
            const segments = path.split('/').map(s => s.trim()).filter(Boolean);
            return segments.length >= 2 && segments[1].toLowerCase() === selectedOrgName;
          })) {
            return true;
          }

          // Method 3: legacy targets array (organizationId may be populated as object)
          const targets = campaign.targets || [];
          if (selectedOrgName && targets.some(t => {
            const orgId = t.organizationId?._id?.toString() || t.organizationId?.toString();
            const orgName = (t.organizationId?.name || t.organizationName || '').toLowerCase();
            return orgId === selectedOrgId || orgName === selectedOrgName;
          })) {
            return true;
          }

          return false;
        });

        setBuCampaigns(filteredCampaigns);
        setBuSelectedCampaignIds(filteredCampaigns.map(c => c._id));
      }
    } catch (error) {
      console.error('Failed to load campaigns for business unit:', error);
      message.error('Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  }, [buSelectedOrganization, buSelectedMonth, buAllOrganizations]);

  // Helper: Fetch campaigns for a specific org and month
  const fetchCampaignsForOrgAndMonth = useCallback(async (orgName, month) => {
    const monthStart = month.startOf('month').toISOString();
    const monthEnd = month.endOf('month').toISOString();

    const params = { 
      limit: 200, 
      status: 'all',
      startDate: monthStart,
      endDate: monthEnd
    };
    
    const res = await campaignService.getAll(params);
    
    if (res.success && Array.isArray(res.data)) {
      const orgNameLower = orgName?.toLowerCase();
      
      return res.data.filter(campaign => {
        const businessUnitNames = campaign.targetPaths?.summary?.businessUnitNames || [];
        if (businessUnitNames.some(name => name?.toLowerCase() === orgNameLower)) {
          return true;
        }
        
        const displayPaths = campaign.targetPaths?.displayPaths || [];
        const orgNamesFromPaths = displayPaths.map(path => {
          const segments = path.split('/').map(s => s.trim()).filter(Boolean);
          return segments.length >= 2 ? segments[1].toLowerCase() : null;
        }).filter(Boolean);
        
        if (orgNamesFromPaths.some(name => name === orgNameLower)) {
          return true;
        }
        
        const targets = campaign.targets || [];
        if (targets.some(t => t.organizationName?.toLowerCase() === orgNameLower)) {
          return true;
        }
        
        return false;
      });
    }
    return [];
  }, []);

  // Helper: Fetch stats for campaigns
  const fetchCampaignStats = useCallback(async (campaigns, orgName) => {
    const filterParams = { organization: orgName };
    
    const results = await Promise.allSettled(
      campaigns.map(async (campaign) => {
        const [statsResult, targetsResult] = await Promise.allSettled([
          campaignService.getCampaignStatistics(campaign._id, filterParams),
          campaignService.getCampaignTargets(campaign._id, { 
            fields: 'participantId,status,score,totalTimeSpent,completedAt',
            limit: 10000,
            ...filterParams
          })
        ]);

        const stats = statsResult.status === 'fulfilled' && statsResult.value?.success
          ? statsResult.value.data
          : null;
        const targets = targetsResult.status === 'fulfilled' && targetsResult.value?.success
          ? targetsResult.value.data?.targets || []
          : [];

        return { id: campaign._id, name: campaign.name, stats, targets };
      })
    );

    return results
      .filter((res) => res.status === 'fulfilled' && res.value?.stats)
      .map((res) => res.value);
  }, []);

  // Helper: Build a per-campaign leaderboard (score + time) and hardest-question
  // ranking, for the "Campaign Details" section — one sub-page per campaign,
  // matching the layout/format already used by the network-level leaderboard.
  const buildCampaignBreakdown = useCallback(async (campaignList = []) => {
    if (!campaignList.length) return [];

    const results = await Promise.all(campaignList.map(async (campaign) => {
      const [targetsResult, questionResult] = await Promise.allSettled([
        campaignService.getCampaignTargets(campaign._id, {
          fields: 'participantId,status,score,totalTimeSpent',
          limit: 2000
        }),
        campaignService.getCampaignQuestionAnalysis(campaign._id)
      ]);

      const targets = targetsResult.status === 'fulfilled' && targetsResult.value?.success
        ? targetsResult.value.data?.targets || []
        : [];
      const questionData = questionResult.status === 'fulfilled' && questionResult.value?.success
        ? questionResult.value.data
        : null;

      const leaderboard = targets
        .filter(t => t.status === 'completed')
        .map(t => ({
          name: `${t.participantId?.name || ''} ${t.participantId?.surname || ''}`.trim() || t.participantId?.email || 'Unknown',
          score: Number(t.score) || 0,
          timeSpent: Number(t.totalTimeSpent) || 0
        }))
        .sort((a, b) => b.score - a.score)
        .map((person, index) => ({
          ...person,
          rank: index + 1,
          scoreDisplay: formatPercentDisplay(person.score, 0),
          // Same h/m formatting already used by the network leaderboard, for consistency
          timeSpentDisplay: (() => {
            const s = Math.round(person.timeSpent);
            if (s <= 0) return '—';
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            if (h > 0) return `${h}h:${String(m).padStart(2, '0')}m`;
            return m > 0 ? `${m}m` : '<1m';
          })()
        }));

      // allQuestions already comes back sorted ascending by successRate (hardest first)
      const questions = (questionData?.allQuestions || []).map(q => ({
        questionText: q.questionText || 'Question',
        successRate: q.successRate,
        successRateDisplay: formatPercentDisplay(q.successRate, 0),
        totalAnswers: q.totalAnswers,
        correctAnswers: q.correctAnswers
      }));

      return {
        campaignId: campaign._id,
        campaignName: campaign.name,
        leaderboard,
        questions
      };
    }));

    return results;
  }, [formatPercentDisplay]);

  // Helper: Calculate simple overview stats (completion rate & avg score)
  const calculateSimpleStats = useCallback((statsData) => {
    if (!statsData || statsData.length === 0) {
      return { completionRate: 0, averageScore: 0 };
    }

    const uniqueParticipantIds = new Set();
    const uniqueCompleted = new Set();
    const personCampaignStats = new Map();

    statsData.forEach((item) => {
      const targets = item?.targets || [];
      targets.forEach(target => {
        const participantId = target.participantId?.email || target.participantId?._id;
        if (participantId) {
          uniqueParticipantIds.add(participantId);

          if (!personCampaignStats.has(participantId)) {
            personCampaignStats.set(participantId, { assigned: 0, completed: 0, score: 0 });
          }
          const personStats = personCampaignStats.get(participantId);
          personStats.assigned += 1;

          if (target.status === 'completed') {
            uniqueCompleted.add(participantId);
            personStats.completed += 1;
            personStats.score += Number(target.score) || 0;
          }
        }
      });
    });

    // Per-person completion rate (same formula as BU overview)
    let totalPersonCompletionRates = 0;
    personCampaignStats.forEach((stats) => {
      if (stats.assigned > 0) {
        totalPersonCompletionRates += (stats.completed / stats.assigned) * 100;
      }
    });
    const completionRate = roundTo(personCampaignStats.size > 0
      ? totalPersonCompletionRates / personCampaignStats.size
      : 0);

    // Per-person average score using same formula as leaderboard: score / assigned (not
    // / completed). Include everyone with an assignment, same population as completionRate
    // above, so a zero-completion person contributes a 0 instead of being dropped.
    let scoreSum = 0;
    let scoreCount = 0;
    personCampaignStats.forEach((stats) => {
      if (stats.assigned > 0) {
        scoreSum += stats.score / stats.assigned;
        scoreCount += 1;
      }
    });
    const averageScore = roundTo(scoreCount > 0 ? scoreSum / scoreCount : 0);

    return { completionRate, averageScore };
  }, []);

  // Build BU Overview aggregation
  const buildBuOverview = useCallback((items = []) => {
    const aggregate = {
      totalTargets: 0,
      uniqueParticipants: 0,
      completed: 0,
      inProgress: 0,
      pending: 0,
      expired: 0,
      excluded: 0,
      averageScore: 0,
      completionRate: 0,
      responseRate: 0,
      totalTimeSpent: 0,
      averageTimeSpent: 0,
      targetDelivery: { attempted: 0, delivered: 0, deliveryRate: '0%' }
    };

    let weightedScoreSum = 0;
    let weightedScoreCount = 0;
    let totalTimeSpentSeconds = 0;
    let timeSpentCount = 0;

    // Track unique participants across all campaigns
    const uniqueParticipantIds = new Set();
    const uniqueCompleted = new Set();
    const uniqueInProgress = new Set();

    // Track per-person campaign completion for average calculation
    const personCampaignStats = new Map(); // email -> { assigned: 0, completed: 0 }

    items.forEach((item) => {
      const overview = item?.stats?.overview || {};
      aggregate.totalTargets += overview.total || 0;

      // Target delivery (from global stats — org filter not supported by stats endpoint)
      aggregate.targetDelivery.attempted += overview.targetDelivery?.attempted || 0;
      aggregate.targetDelivery.delivered += overview.targetDelivery?.delivered || 0;

      // Count unique participants from org-filtered targets
      const targets = item?.targets || [];
      targets.forEach(target => {
        const participantId = target.participantId?.email || target.participantId?._id;
        if (participantId) {
          uniqueParticipantIds.add(participantId);

          // Track per-person campaign stats
          if (!personCampaignStats.has(participantId)) {
            personCampaignStats.set(participantId, { assigned: 0, completed: 0, score: 0 });
          }
          const personStats = personCampaignStats.get(participantId);
          personStats.assigned += 1;

          if (target.status === 'completed') {
            uniqueCompleted.add(participantId);
            personStats.completed += 1;
            personStats.score += Number(target.score) || 0;

            // Accumulate time from individual targets (totalTimeSpent is in seconds)
            const t = Number(target.totalTimeSpent) || 0;
            if (t > 0) {
              totalTimeSpentSeconds += t;
              timeSpentCount += 1;
            }
          } else if (target.status === 'in-progress') {
            uniqueInProgress.add(participantId);
          }
        }
      });
    });

    // Compute average score using the same per-person formula as the leaderboard:
    // each person contributes (totalScore / campaignsAssigned), averaged across persons.
    // This ensures the overview average can never exceed the top leaderboard score.
    // Include everyone with an assignment (not just stats.completed > 0) so a
    // zero-completion person correctly contributes a 0 — matching completionRate's
    // population below. Excluding them here silently inflated the headline score.
    personCampaignStats.forEach((stats) => {
      if (stats.assigned > 0) {
        weightedScoreSum += stats.score / stats.assigned;
        weightedScoreCount += 1;
      }
    });

    // Use unique counts
    aggregate.uniqueParticipants = uniqueParticipantIds.size;
    aggregate.completed = uniqueCompleted.size;
    aggregate.inProgress = uniqueInProgress.size;
    aggregate.pending = aggregate.uniqueParticipants - aggregate.completed - aggregate.inProgress;
    if (aggregate.pending < 0) aggregate.pending = 0;

    // Calculate per-campaign average completion rate
    // For each person: (completed campaigns / assigned campaigns) * 100, then average across all people
    let totalPersonCompletionRates = 0;
    personCampaignStats.forEach((stats) => {
      if (stats.assigned > 0) {
        totalPersonCompletionRates += (stats.completed / stats.assigned) * 100;
      }
    });
    aggregate.completionRate = roundTo(personCampaignStats.size > 0 
      ? totalPersonCompletionRates / personCampaignStats.size 
      : 0);

    aggregate.averageScore = roundTo(weightedScoreCount > 0 ? (weightedScoreSum / weightedScoreCount) : 0);
    aggregate.responseRate = roundTo(aggregate.uniqueParticipants > 0 ? ((aggregate.completed + aggregate.inProgress) / aggregate.uniqueParticipants) * 100 : 0);
    // totalTimeSpentSeconds is in seconds; divide by 60 to get minutes
    aggregate.averageTimeSpent = roundTo(timeSpentCount > 0 ? (totalTimeSpentSeconds / timeSpentCount / 60) : 0, 1);
    
    if (aggregate.targetDelivery.attempted > 0) {
      const rate = roundTo((aggregate.targetDelivery.delivered / aggregate.targetDelivery.attempted) * 100);
      aggregate.targetDelivery.deliveryRate = `${rate.toFixed(2)}%`;
    }

    // Format displays
    aggregate.completionRateDisplay = formatPercentDisplay(aggregate.completionRate, 2);
    aggregate.responseRateDisplay = formatPercentDisplay(aggregate.responseRate, 2);
    aggregate.averageScoreDisplay = formatPercentDisplay(aggregate.averageScore, 2);
    aggregate.averageTimeSpentDisplay = `${aggregate.averageTimeSpent.toFixed(1)} min`;

    return aggregate;
  }, [formatPercentDisplay]);

  // Build leaderboard from targets - aggregate per person across all campaigns
  const buildBuLeaderboard = useCallback((items = []) => {
    // Use a Map to aggregate results per person (keyed by email)
    const personMap = new Map();
    const totalCampaigns = items.length; // Total number of campaigns
    
    // First pass: track all participants and their assigned campaigns
    items.forEach((item) => {
      const targets = item?.targets || [];
      targets.forEach(target => {
        if (target.participantId) {
          const participant = target.participantId;
          const email = participant.email || participant._id || 'unknown';
          const name = participant.name && participant.surname 
            ? `${participant.name} ${participant.surname}`
            : participant.name || participant.email || 'Unknown';
          
          if (!personMap.has(email)) {
            personMap.set(email, {
              name,
              email,
              department: participant.department || 'No Department',
              totalScore: 0,
              totalTimeSpent: 0,
              campaignsCompleted: 0,
              campaignsAssigned: 0,
              completedAt: null
            });
          }
          
          const person = personMap.get(email);
          person.campaignsAssigned += 1;
          
          if (target.status === 'completed') {
            person.totalScore += Number(target.score) || 0;
            person.totalTimeSpent += Number(target.totalTimeSpent) || 0;
            person.campaignsCompleted += 1;
            // Keep the most recent completion date
            if (target.completedAt && (!person.completedAt || new Date(target.completedAt) > new Date(person.completedAt))) {
              person.completedAt = target.completedAt;
            }
          }
        }
      });
    });

    // Convert to array and calculate averages
    const allPersons = Array.from(personMap.values());

    const nonStarters = allPersons
      .filter(person => {
        const cr = person.campaignsAssigned > 0 ? (person.campaignsCompleted / person.campaignsAssigned) * 100 : 0;
        return cr < 33;
      })
      .map(person => {
        const cr = roundTo(person.campaignsAssigned > 0 ? (person.campaignsCompleted / person.campaignsAssigned) * 100 : 0);
        const score = roundTo(person.campaignsAssigned > 0 ? person.totalScore / person.campaignsAssigned : 0);
        return {
          name: person.name,
          email: person.email,
          department: person.department,
          campaignsAssigned: person.campaignsAssigned,
          campaignsCompleted: person.campaignsCompleted,
          completionRate: cr,
          completionRateDisplay: `${Math.round(cr)}%`,
          score,
          scoreDisplay: `${Math.round(score)}%`,
        };
      })
      .sort((a, b) => a.completionRate - b.completionRate);

    const aggregatedList = allPersons
      .filter(person => person.campaignsCompleted > 0)
      .map(person => ({
        name: person.name,
        email: person.email,
        department: person.department,
        // Divide by assigned (not completed) so unfinished campaigns count as 0
        score: roundTo(person.campaignsAssigned > 0 ? person.totalScore / person.campaignsAssigned : 0),
        rawScore: roundTo(person.campaignsCompleted > 0 ? person.totalScore / person.campaignsCompleted : 0),
        timeSpent: person.totalTimeSpent,
        averageTimeSpent: roundTo(person.campaignsCompleted > 0 ? person.totalTimeSpent / person.campaignsCompleted : 0),
        campaignsCompleted: person.campaignsCompleted,
        campaignsAssigned: person.campaignsAssigned,
        completionRate: roundTo(person.campaignsAssigned > 0 ? (person.campaignsCompleted / person.campaignsAssigned) * 100 : 0),
        completedAt: person.completedAt
    }));

    // Sort by score descending, then by time ascending
    aggregatedList.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.averageTimeSpent - b.averageTimeSpent;
    });

    // Add rank and display values
    const leaderboard = aggregatedList.map((person, index) => ({
      ...person,
      rank: index + 1,
      scoreDisplay: formatPercentDisplay(person.score, 0),
      timeSpentDisplay: (() => {
        const s = Math.round(Number(person.averageTimeSpent) || 0);
        if (s <= 0) return '—';
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        if (h > 0) return `${h}h:${String(m).padStart(2, '0')}m`;
        return m > 0 ? `${m}m` : '<1m';
      })(),
      completionRateDisplay: formatPercentDisplay(person.completionRate, 0),
      campaignsCompletedDisplay: person.campaignsCompleted.toString(),
      campaignsAssignedDisplay: person.campaignsAssigned.toString()
    }));

    // Limit charts to participants with completion rate >= 33%
    const eligibleParticipants = leaderboard.filter(person => person.completionRate >= 33);

    // Build chart data (top 20 by score among eligible participants)
    const top20ByScore = eligibleParticipants.slice(0, 20);
    
    // Score chart (sorted by score high to low)
    const scoreChart = {
      labels: top20ByScore.map(t => t.name),
      datasets: [{
        label: 'Average Score',
        data: top20ByScore.map(t => t.score),
        dataLabels: top20ByScore.map(t => t.scoreDisplay)
      }]
    };

    // Sort by completion rate for completion rate chart (among eligible participants)
    const sortedByCompletionRate = [...eligibleParticipants].sort((a, b) => b.completionRate - a.completionRate);
    const top20ByCompletionRate = sortedByCompletionRate.slice(0, 20);
    
    // Completion rate chart (sorted by completion rate high to low)
    const completionRateChart = {
      labels: top20ByCompletionRate.map(t => t.name),
      datasets: [{
        label: 'Completion Rate',
        data: top20ByCompletionRate.map(t => t.completionRate),
        dataLabels: top20ByCompletionRate.map(t => t.completionRateDisplay)
      }]
    };

    return {
      data: eligibleParticipants,
      totalParticipants: eligibleParticipants.length,
      totalQualified: eligibleParticipants.length,
      totalWithCompletions: leaderboard.length,
      nonStarters,
      scoreChart,
      completionRateChart,
    };
  }, [formatPercentDisplay]);

  // Build department stats for BU report. Uses the same effective, per-person method as
  // buildBuOverview (completed/assigned averaged across people, not raw headcount-completed
  // ÷ headcount-total) so a department's CR/Score means the same thing as everywhere else
  // in the report — previously this used a different "participation" method (unique people
  // who completed >=1 campaign ÷ total people; average of completed quizzes only), which
  // produced a materially different number under the same "Completion Rate"/"Score" labels.
  const buildBuDepartments = useCallback((items = []) => {
    const deptMap = new Map();

    items.forEach((item) => {
      const targets = item?.targets || [];
      targets.forEach(target => {
        const dept = target.participantId?.department || 'No Department';
        const personId = target.participantId?._id || target.participantId?.email || String(target.participantId);
        if (!personId) return;

        if (!deptMap.has(dept)) {
          deptMap.set(dept, { department: dept, persons: new Map() });
        }
        const persons = deptMap.get(dept).persons;
        if (!persons.has(personId)) {
          persons.set(personId, { assigned: 0, completed: 0, score: 0 });
        }
        const p = persons.get(personId);
        p.assigned += 1;
        if (target.status === 'completed') {
          p.completed += 1;
          p.score += Number(target.score) || 0;
        }
      });
    });

    // Convert to array and calculate effective rates (per-person, averaged across staff)
    const departments = Array.from(deptMap.values()).map(dept => {
      const persons = Array.from(dept.persons.values());
      const total = persons.length;
      const completed = persons.filter(p => p.completed > 0).length;

      let crSum = 0, scoreSum = 0;
      persons.forEach(p => {
        if (p.assigned > 0) {
          crSum += (p.completed / p.assigned) * 100;
          scoreSum += p.score / p.assigned;
        }
      });
      const cr = roundTo(total > 0 ? crSum / total : 0);
      const avgScore = roundTo(total > 0 ? scoreSum / total : 0);

      return {
        department: dept.department,
        total,
        completed,
        completionRate: cr,
        completionRateDisplay: formatPercentDisplay(cr, 2),
        averageScore: avgScore,
        averageScoreDisplay: formatPercentDisplay(avgScore, 2),
      };
    });

    // Sort by completion rate descending for completion rate chart
    const sortedByCompletionRate = [...departments].sort((a, b) => b.completionRate - a.completionRate);
    const top10ByCompletionRate = sortedByCompletionRate.slice(0, 10);
    
    // Completion rate per department chart (sorted high to low)
    const completionRateChart = {
      labels: top10ByCompletionRate.map(d => d.department),
      datasets: [{
        label: 'Completion Rate',
        data: top10ByCompletionRate.map(d => d.completionRate),
        dataLabels: top10ByCompletionRate.map(d => d.completionRateDisplay)
      }]
    };

    // Sort by score descending for score chart
    const sortedByScore = [...departments].sort((a, b) => b.averageScore - a.averageScore);
    const top10ByScore = sortedByScore.slice(0, 10);
    
    // Average score per department chart (sorted high to low)
    const scoreChart = {
      labels: top10ByScore.map(d => d.department),
      datasets: [{
        label: 'Average Score',
        data: top10ByScore.map(d => d.averageScore),
        dataLabels: top10ByScore.map(d => d.averageScoreDisplay)
      }]
    };

    // Sort by people count descending for people chart
    const sortedByPeople = [...departments].sort((a, b) => b.total - a.total);
    const top10ByPeople = sortedByPeople.slice(0, 10);
    
    // People count chart (sorted high to low)
    const peopleChart = {
      labels: top10ByPeople.map(d => d.department),
      datasets: [{
        label: 'People Count',
        data: top10ByPeople.map(d => d.total),
        dataLabels: top10ByPeople.map(d => d.total.toString())
      }]
    };

    return {
      data: departments,
      completionRateChart,
      scoreChart,
      peopleChart
    };
  }, [formatPercentDisplay]);

  const buildBuAllParticipants = useCallback((items = []) => {
    const personMap = new Map();
    items.forEach((item) => {
      (item?.targets || []).forEach(target => {
        const participant = target.participantId;
        if (!participant) return;
        const email = participant.email || participant._id || 'unknown';
        const name = participant.name && participant.surname
          ? `${participant.name} ${participant.surname}`
          : participant.name || participant.email || 'Unknown';
        if (!personMap.has(email)) {
          personMap.set(email, { name, department: participant.department || '', totalScore: 0, campaignsCompleted: 0, isCompleted: false });
        }
        const p = personMap.get(email);
        if (target.status === 'completed') {
          p.totalScore += Number(target.score) || 0;
          p.campaignsCompleted += 1;
          p.isCompleted = true;
        }
      });
    });
    return Array.from(personMap.values())
      .map(p => ({
        name: p.name,
        department: p.department,
        isCompleted: p.isCompleted,
        score: p.campaignsCompleted > 0 ? roundTo(p.totalScore / p.campaignsCompleted) : 0,
        scoreDisplay: p.campaignsCompleted > 0 ? formatPercentDisplay(roundTo(p.totalScore / p.campaignsCompleted), 0) : '0%',
      }))
      .sort((a, b) => b.score - a.score);
  }, [formatPercentDisplay]);

  // Generate Business Unit Report
  const generateBuReport = useCallback(async () => {
    const activeBuCampaigns = buCampaigns.filter(c => buSelectedCampaignIds.includes(c._id));
    if (!buSelectedOrganization || activeBuCampaigns.length === 0) {
      message.warning('Please select an organization and ensure there are campaigns');
      return;
    }

    try {
      setBuReportLoading(true);

      const selectedOrgName = buAllOrganizations.find(o => o.id === buSelectedOrganization)?.name;
      const selectedOrg = buAllOrganizations.find(o => o.id === buSelectedOrganization);
      const managingGroup = selectedOrg?.managingGroup;

      // Set report name
      setBuReportName(`${selectedOrgName} - ${buSelectedMonth?.format('MMMM YYYY')} Analysis Report`);

      // Fetch detailed stats for each campaign (current month)
      const campaignIds = activeBuCampaigns.map(c => c._id);
      const filterParams = { organization: selectedOrgName };

      const results = await Promise.allSettled(
        campaignIds.map(async (campaignId) => {
          const [statsResult, questionsResult, targetsResult] = await Promise.allSettled([
            campaignService.getCampaignStatistics(campaignId, filterParams),
            campaignService.getCampaignQuestionAnalysis(campaignId, filterParams),
            campaignService.getCampaignTargets(campaignId, {
              limit: 2000,
              ...filterParams
            })
          ]);

          const stats = statsResult.status === 'fulfilled' && statsResult.value?.success
            ? statsResult.value.data
            : null;
          const questions = questionsResult.status === 'fulfilled' && questionsResult.value?.success
            ? questionsResult.value.data
            : null;
          const targets = targetsResult.status === 'fulfilled' && targetsResult.value?.success
            ? targetsResult.value.data?.targets || []
            : [];

          const campaign = activeBuCampaigns.find((c) => c._id === campaignId);
          const name = campaign?.name || 'Campaign';

          return { id: campaignId, name, stats, questions, targets };
        })
      );

      const successful = results
        .filter((res) => res.status === 'fulfilled' && res.value?.stats)
        .map((res) => res.value);

      if (!successful.length) {
        message.error('No campaign stats could be loaded');
        setBuReportLoading(false);
        return;
      }

      // ======== NETWORK AVERAGE (orgs under the same Group Owner) ========
      // Find Group Owner for the selected org's managing group, then average across
      // all managing groups that belong to that same Group Owner.
      const groupOwnerEntry = mcAllGroupOwners.find(o =>
        managingGroup && o.managingGroups.includes(managingGroup)
      );
      const ownerManagingGroups = groupOwnerEntry
        ? groupOwnerEntry.managingGroups
        : (managingGroup ? [managingGroup] : []);

      // Build a set of org names belonging to the Group Owner scope
      const ownerOrgNameSet = new Set(
        buAllOrganizations
          .filter(o => ownerManagingGroups.includes(o.managingGroup))
          .map(o => (o.name || '').toLowerCase())
      );

      const computeNetworkAvg = (items) => {
        const orgMap = new Map();
        items.forEach(item => {
          (item.stats?.byOrganization || []).forEach(org => {
            const name = org.organizationName || 'Unknown';
            // Only include orgs in the Group Owner's scope (if we have scope data)
            if (ownerOrgNameSet.size > 0 && !ownerOrgNameSet.has(name.toLowerCase())) return;
            if (!orgMap.has(name)) orgMap.set(name, { total: 0, completed: 0, scoreWeighted: 0 });
            const e = orgMap.get(name);
            e.total += Number(org.totalParticipants) || 0;
            e.completed += Number(org.completedCount) || 0;
            e.scoreWeighted += (Number(org.averageScore) || 0) * (Number(org.completedCount) || 0);
          });
        });
        const orgs = Array.from(orgMap.values()).map(e => ({
          completionRate: e.total > 0 ? (e.completed / e.total) * 100 : 0,
          averageScore: e.completed > 0 ? e.scoreWeighted / e.completed : 0,
        })).filter(o => o.completionRate > 0 || o.averageScore > 0);
        if (!orgs.length) return { completionRate: 0, averageScore: 0, orgCount: 0 };
        return {
          completionRate: roundTo(orgs.reduce((s, o) => s + o.completionRate, 0) / orgs.length),
          averageScore: roundTo(orgs.reduce((s, o) => s + o.averageScore, 0) / orgs.length),
          orgCount: orgs.length,
        };
      };
      const networkAvg = computeNetworkAvg(successful);

      // Build aggregated overview for current month
      const overview = buildBuOverview(successful);
      
      // Build leaderboard from targets
      const leaderboard = buildBuLeaderboard(successful);
      
      // Build department stats
      const departments = buildBuDepartments(successful);

      // Build all participants list for PDF export
      const allParticipants = buildBuAllParticipants(successful);

      // Score distribution buckets (using effective score), over the WHOLE team —
      // leaderboard.data alone is only the 33%-qualified subset, which silently drops
      // zero-completion people from an "X% of team" distribution. nonStarters (cr<33%,
      // includes 0-completion people) + data (cr>=33%) together cover everyone exactly once.
      const qualifiedData = [...(leaderboard.nonStarters || []), ...(leaderboard.data || [])];
      const scoreBuckets = {
        green: qualifiedData.filter(p => p.score >= 80).length,
        amber: qualifiedData.filter(p => p.score >= 60 && p.score < 80).length,
        red: qualifiedData.filter(p => p.score < 60).length,
        total: qualifiedData.length,
      };

      // Per-campaign completion and score breakdown
      const campaignBreakdown = successful.map(item => {
        const targets = item?.targets || [];
        const assigned = targets.length;
        const completedTargets = targets.filter(t => t.status === 'completed');
        const completed = completedTargets.length;
        const avgScore = completed > 0
          ? roundTo(completedTargets.reduce((s, t) => s + (Number(t.score) || 0), 0) / completed)
          : 0;
        return {
          name: item.name || item.campaignName || 'Unnamed Campaign',
          assigned,
          completed,
          completionRate: roundTo(assigned > 0 ? (completed / assigned) * 100 : 0),
          avgScore,
        };
      }).sort((a, b) => a.completionRate - b.completionRate);

      // Per-campaign detail: leaderboard (score + time) and hardest questions, one
      // block per campaign. Reuses the targets/questions already fetched above
      // instead of re-fetching (unlike buildCampaignBreakdown).
      const fmtTimeSpent = (seconds) => {
        const s = Math.round(seconds);
        if (s <= 0) return '—';
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        if (h > 0) return `${h}h:${String(m).padStart(2, '0')}m`;
        return m > 0 ? `${m}m` : '<1m';
      };
      const campaignDetails = successful.map(item => {
        const targets = item?.targets || [];
        const completedRows = targets
          .filter(t => t.status === 'completed')
          .map(t => ({
            name: `${t.participantId?.name || ''} ${t.participantId?.surname || ''}`.trim() || t.participantId?.email || 'Unknown',
            score: Number(t.score) || 0,
            timeSpent: Number(t.totalTimeSpent) || 0,
          }))
          .sort((a, b) => b.score - a.score)
          .map((person, index) => ({
            rank: index + 1,
            name: person.name,
            scoreDisplay: formatPercentDisplay(person.score, 0),
            timeSpentDisplay: fmtTimeSpent(person.timeSpent),
            didNotComplete: false,
          }));

        // Started but never finished (in-progress / expired) — kept in the leaderboard
        // (not the "did not start" list below) but flagged so the PDF can show them
        // explicitly as "Did not complete" with a 0, instead of silently dropping them.
        const incompleteRows = targets
          .filter(t => t.status === 'in-progress' || t.status === 'expired')
          .map(t => ({
            rank: null,
            name: `${t.participantId?.name || ''} ${t.participantId?.surname || ''}`.trim() || t.participantId?.email || 'Unknown',
            scoreDisplay: '0%',
            timeSpentDisplay: 'Did not complete',
            didNotComplete: true,
          }));

        const leaderboard = [...completedRows, ...incompleteRows];

        // allQuestions already comes back sorted ascending by successRate (hardest first)
        const questions = (item?.questions?.allQuestions || [])
          .map(q => ({
            questionText: q.questionText || 'Question',
            successRate: Number(q.successRate) || 0,
            successRateDisplay: formatPercentDisplay(q.successRate, 0),
          }));

        // People who never opened/started this campaign — status 'pending'. Distinct
        // from someone who completed with a genuine 0% score (those stay in the
        // leaderboard above).
        const nonStarters = targets
          .filter(t => t.status === 'pending')
          .map(t => ({
            name: `${t.participantId?.name || ''} ${t.participantId?.surname || ''}`.trim() || t.participantId?.email || 'Unknown',
          }));

        return {
          name: item.name || item.campaignName || 'Unnamed Campaign',
          leaderboard,
          questions,
          nonStarters,
        };
      });

      // Timing analysis: split completions into early / mid / late thirds of the month
      const monthStart = buSelectedMonth.clone().startOf('month').toDate();
      const daysInMonth = buSelectedMonth.daysInMonth();
      const earlyEnd  = new Date(monthStart); earlyEnd.setDate(Math.round(daysInMonth / 3));
      const midEnd    = new Date(monthStart); midEnd.setDate(Math.round(daysInMonth * 2 / 3));
      let eCount = 0, mCount = 0, lCount = 0, eScore = 0, mScore = 0, lScore = 0;
      successful.forEach(item => {
        (item?.targets || []).forEach(t => {
          if (t.status === 'completed' && t.completedAt) {
            const d = new Date(t.completedAt);
            const sc = Number(t.score) || 0;
            if (d <= earlyEnd)     { eCount++; eScore += sc; }
            else if (d <= midEnd)  { mCount++; mScore += sc; }
            else                   { lCount++; lScore += sc; }
          }
        });
      });
      const tTotal = eCount + mCount + lCount;
      const timingStats = tTotal > 0 ? {
        earlyPct:     Math.round((eCount / tTotal) * 100),
        midPct:       Math.round((mCount / tTotal) * 100),
        latePct:      Math.round((lCount / tTotal) * 100),
        earlyAvgScore: eCount > 0 ? Math.round(eScore / eCount) : null,
        midAvgScore:   mCount > 0 ? Math.round(mScore / mCount) : null,
        lateAvgScore:  lCount > 0 ? Math.round(lScore / lCount) : null,
        total: tTotal,
      } : null;

      // ======== 3-MONTH AVERAGE CALCULATION ========
      // Get previous 2 months
      const month1 = buSelectedMonth.clone().subtract(1, 'month');
      const month2 = buSelectedMonth.clone().subtract(2, 'month');
      
      // Fetch campaigns for previous months
      const [campaigns1, campaigns2] = await Promise.all([
        fetchCampaignsForOrgAndMonth(selectedOrgName, month1),
        fetchCampaignsForOrgAndMonth(selectedOrgName, month2)
      ]);

      // Fetch stats for previous months
      const [stats1, stats2] = await Promise.all([
        campaigns1.length > 0 ? fetchCampaignStats(campaigns1, selectedOrgName) : [],
        campaigns2.length > 0 ? fetchCampaignStats(campaigns2, selectedOrgName) : []
      ]);

      // Calculate stats for each month
      const currentMonthStats = calculateSimpleStats(successful);
      const month1Stats = calculateSimpleStats(stats1);
      const month2Stats = calculateSimpleStats(stats2);

      // Calculate 3-month average (only include months with data)
      const monthsWithData = [currentMonthStats, month1Stats, month2Stats].filter(
        s => s.completionRate > 0 || s.averageScore > 0
      );
      
      const threeMonthAvg = {
        completionRate: roundTo(
          monthsWithData.length > 0
            ? monthsWithData.reduce((sum, s) => sum + s.completionRate, 0) / monthsWithData.length
            : 0
        ),
        averageScore: roundTo(
          monthsWithData.length > 0
            ? monthsWithData.reduce((sum, s) => sum + s.averageScore, 0) / monthsWithData.length
            : 0
        )
      };

      // ======== NETWORK / GROUP AVERAGE CALCULATION ========
      // Scope: if the org's managing group belongs to a group owner, use ALL orgs
      // under that owner (across all its managing groups). Otherwise fall back to
      // just the direct managing group.
      let managingGroupAvg = { completionRate: 0, averageScore: 0 };

      if (managingGroup) {
        const groupOrgs = buAllOrganizations.filter(o => {
          if (o.id === buSelectedOrganization) return false;
          if (groupOwnerEntry) return ownerManagingGroups.includes(o.managingGroup);
          return o.managingGroup === managingGroup;
        });

        if (groupOrgs.length > 0) {
          // Fetch campaigns and stats for each org in the group (current month only)
          const groupStatsPromises = groupOrgs.map(async (org) => {
            const orgCampaigns = await fetchCampaignsForOrgAndMonth(org.name, buSelectedMonth);
            if (orgCampaigns.length > 0) {
              const orgStats = await fetchCampaignStats(orgCampaigns, org.name);
              return calculateSimpleStats(orgStats);
            }
            return { completionRate: 0, averageScore: 0 };
          });

          const groupStatsResults = await Promise.all(groupStatsPromises);
          
          // Include current org in group average
          const allGroupStats = [currentMonthStats, ...groupStatsResults].filter(
            s => s.completionRate > 0 || s.averageScore > 0
          );

          managingGroupAvg = {
            completionRate: roundTo(
              allGroupStats.length > 0
                ? allGroupStats.reduce((sum, s) => sum + s.completionRate, 0) / allGroupStats.length
                : 0
            ),
            averageScore: roundTo(
              allGroupStats.length > 0
                ? allGroupStats.reduce((sum, s) => sum + s.averageScore, 0) / allGroupStats.length
                : 0
            )
          };
        } else {
          // Only org in the group, use current stats
          managingGroupAvg = currentMonthStats;
        }
      }

      const buData = {
        organization: {
          id: buSelectedOrganization,
          name: selectedOrgName,
          departments: selectedOrg?.departments || [],
          participantCount: selectedOrg?.participantCount || 0,
          managingGroup: managingGroup || '',
          groupOwner: groupOwnerEntry?.name || ''
        },
        period: {
          month: buSelectedMonth?.format('MMMM'),
          year: buSelectedMonth?.format('YYYY'),
          display: buSelectedMonth?.format('MMMM YYYY')
        },
        campaigns: activeBuCampaigns.map(c => ({
          id: c._id,
          name: c.name,
          status: c.status,
          startDate: c.startDate,
          endDate: c.endDate
        })),
        overview,
        leaderboard,
        departments,
        allParticipants,
        networkAvg: managingGroupAvg,
        nonStarters: leaderboard.nonStarters || [],
        scoreBuckets,
        campaignBreakdown,
        campaignDetails,
        timingStats,
        // Comparison data (in order: oldest month, previous month, current month, group avg, benchmark)
        comparison: {
          month2: {
            label: month2.format('MMMM'),
            completionRate: month2Stats.completionRate,
            completionRateDisplay: formatPercentDisplay(month2Stats.completionRate, 2),
            averageScore: month2Stats.averageScore,
            averageScoreDisplay: formatPercentDisplay(month2Stats.averageScore, 2)
          },
          month1: {
            label: month1.format('MMMM'),
            completionRate: month1Stats.completionRate,
            completionRateDisplay: formatPercentDisplay(month1Stats.completionRate, 2),
            averageScore: month1Stats.averageScore,
            averageScoreDisplay: formatPercentDisplay(month1Stats.averageScore, 2)
          },
          currentMonth: {
            label: buSelectedMonth?.format('MMMM'),
            completionRate: currentMonthStats.completionRate,
            completionRateDisplay: formatPercentDisplay(currentMonthStats.completionRate, 2),
            averageScore: currentMonthStats.averageScore,
            averageScoreDisplay: formatPercentDisplay(currentMonthStats.averageScore, 2)
          },
          managingGroupAvg: {
            label: 'Group Avg',
            completionRate: managingGroupAvg.completionRate,
            completionRateDisplay: formatPercentDisplay(managingGroupAvg.completionRate, 2),
            averageScore: managingGroupAvg.averageScore,
            averageScoreDisplay: formatPercentDisplay(managingGroupAvg.averageScore, 2)
          },
          benchmarks: {
            label: 'Benchmark',
            completionRate: buCompletionRateBenchmark,
            completionRateDisplay: formatPercentDisplay(buCompletionRateBenchmark, 0),
            averageScore: buScoreBenchmark,
            averageScoreDisplay: formatPercentDisplay(buScoreBenchmark, 0)
          }
        }
      };

      setBuReportData(buData);
      setBuWizardStep(1);
      message.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating BU report:', error);
      message.error('Failed to generate report');
    } finally {
      setBuReportLoading(false);
    }
  }, [buSelectedOrganization, buCampaigns, buSelectedCampaignIds, buSelectedMonth, buAllOrganizations, mcAllGroupOwners, buildBuOverview, buildBuLeaderboard, buildBuDepartments, buildBuAllParticipants, fetchCampaignsForOrgAndMonth, fetchCampaignStats, calculateSimpleStats, formatPercentDisplay, buCompletionRateBenchmark, buScoreBenchmark]);

  // Load business units when report type changes
  useEffect(() => {
    if (selectedReportType === 'business-unit') {
      loadBusinessUnits();
    }
  }, [selectedReportType, loadBusinessUnits]);

  // Load campaigns when org or month changes
  useEffect(() => {
    if (selectedReportType === 'business-unit' && buSelectedOrganization && buSelectedMonth) {
      loadBuCampaigns();
    }
  }, [selectedReportType, buSelectedOrganization, buSelectedMonth, loadBuCampaigns]);

  // ======== END BUSINESS UNIT REPORT FUNCTIONS ========

  // Handle canceling grouping config
  const handleCancelGroupingConfig = () => {
    setShowGroupingConfig(false);
    setEditableGroups([]);
    setReportData(null);
  };

  // Load a saved report's config into the multi-campaign builder
  const loadReportConfigIntoBuilder = (savedReport, bumpMonthByOne = false) => {
    const cfg = savedReport.config || {};
    if (cfg.managingGroups?.length) setMcSelectedManagingGroup(cfg.managingGroups);
    if (cfg.groupOwner) setMcSelectedGroupOwner(cfg.groupOwner);
    if (cfg.pdfOptions) setMcPdfOptions(prev => ({ ...prev, ...cfg.pdfOptions }));
    if (cfg.reportName) setReportName(cfg.reportName);
    if (cfg.editableGroups?.length) setMcRestoreGroups(cfg.editableGroups);

    if (cfg.month) {
      let m = dayjs(cfg.month);
      if (bumpMonthByOne) m = m.add(1, 'month');
      setMcSelectedMonth(m);
    }

    // Clear previous results — user will need to select campaigns and Load
    setSelectedCampaignIds([]);
    setReportData(null);
    setMcLoadedFromReport({ title: savedReport.title, id: savedReport._id });
    setSelectedReportType('multi-campaign');
  };

  const handleLoadPreset = (presetName) => {
    const preset = availableDeptPresets.find(p => p.name === presetName);
    if (!preset) return;
    const loaded = preset.groups.map((g, i) => ({
      id: `preset_${g.key || i}_${Date.now()}`,
      key: g.key,
      label: g.label,
      name: g.label,
      isCustom: true,
      departments: (g.departments || []).filter(d => availableDepartments.includes(d))
    }));
    setEditableGroups(loaded);
    setLoadedPresetName(presetName);
    message.success(`Preset "${presetName}" loaded — ${loaded.length} groups`);
  };

  const handleUpdatePreset = async () => {
    if (!loadedPresetName) return;
    const groups = editableGroups.map(g => ({
      key: g.key || g.id,
      label: g.label || g.name,
      departments: g.departments || []
    }));
    const result = await settingsService.updateDepartmentGroupPreset(loadedPresetName, loadedPresetName, groups);
    if (result.success) {
      message.success(`Preset "${loadedPresetName}" updated`);
      const refreshed = await settingsService.getDepartmentGroupPresets();
      if (refreshed.success) {
        const presets = refreshed.data?.data || refreshed.data || [];
        setAvailableDeptPresets(Array.isArray(presets) ? presets : []);
      }
    } else {
      message.error(result.message || 'Failed to update preset');
    }
  };

  const handleDeletePreset = async () => {
    if (!loadedPresetName) return;
    const result = await settingsService.removeDepartmentGroupPreset(loadedPresetName);
    if (result.success) {
      message.success(`Preset "${loadedPresetName}" deleted`);
      setLoadedPresetName(null);
      const refreshed = await settingsService.getDepartmentGroupPresets();
      if (refreshed.success) {
        const presets = refreshed.data?.data || refreshed.data || [];
        setAvailableDeptPresets(Array.isArray(presets) ? presets : []);
      }
    } else {
      message.error(result.message || 'Failed to delete preset');
    }
  };

  const handleSaveAsPreset = async () => {
    if (!savePresetName.trim()) { message.error('Enter a preset name'); return; }
    const groups = editableGroups.map(g => ({
      key: g.key || g.id,
      label: g.label || g.name,
      departments: g.departments || []
    }));
    const result = await settingsService.addDepartmentGroupPreset(savePresetName.trim(), groups);
    if (result.success) {
      message.success(`Preset "${savePresetName.trim()}" saved`);
      setSavePresetModalOpen(false);
      setSavePresetName('');
      // Re-fetch the full presets list so the dropdown stays up to date
      const refreshed = await settingsService.getDepartmentGroupPresets();
      if (refreshed.success) {
        const presets = refreshed.data?.data || refreshed.data || [];
        setAvailableDeptPresets(Array.isArray(presets) ? presets : []);
      }
    } else {
      message.error(result.message);
    }
  };


  const renderReportCards = () => (
    <Row gutter={[16, 16]}>
      {reportCards.map(card => (
        <Col key={card.key} xs={24} sm={12} md={8}>
          <Card
            hoverable
            onClick={() => setSelectedReportType(card.key)}
            style={{
              textAlign: 'center',
              height: '100%',
              cursor: 'pointer',
              border: selectedReportType === card.key ? '2px solid #2E86AB' : undefined
            }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {card.icon}
              <div>
                <Title level={4} style={{ margin: 0 }}>{card.title}</Title>
                <Text type="secondary">{card.description}</Text>
              </div>
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Render Business Unit Report Interface
  const renderBusinessUnitInterface = () => (
    <Card
      title="Business Unit Report"
      extra={
        <Button onClick={() => {
          setSelectedReportType(null);
          setBuSelectedOrganization(null);
          setBuSelectedMonth(null);
          setBuCampaigns([]);
          setBuReportData(null);
          setBuWizardStep(0);
        }}>
          Back to Reports
        </Button>
      }
    >
      <Steps
        current={buWizardStep}
        style={{ marginBottom: 32 }}
        items={[
          { title: 'Select Business Unit', description: 'Organisation, month & settings' },
          { title: 'Report Preview', description: 'View & download' },
        ]}
      />

      {/* ── Step 0: Select Data ── */}
      {buWizardStep === 0 && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={6}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Select Organisation:
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Select organisation"
                value={buSelectedOrganization}
                onChange={(value) => {
                  setBuSelectedOrganization(value);
                  setBuReportData(null);
                  setBuWizardStep(0);
                }}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={buAllOrganizations.map(org => ({
                  value: org.id,
                  label: org.name
                }))}
              />
            </Col>
            <Col span={6}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Select Month:
              </Text>
              <DatePicker
                picker="month"
                style={{ width: '100%' }}
                value={buSelectedMonth}
                onChange={(date) => {
                  setBuSelectedMonth(date);
                  setBuReportData(null);
                  setBuWizardStep(0);
                }}
                placeholder="Select month"
                disabledDate={(current) => current && current > dayjs().endOf('month')}
              />
            </Col>
            <Col span={4}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Completion Benchmark:
              </Text>
              <InputNumber
                style={{ width: '100%' }}
                min={0} max={100}
                value={buCompletionRateBenchmark}
                onChange={(value) => setBuCompletionRateBenchmark(value || 0)}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
              />
            </Col>
            <Col span={4}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Score Benchmark:
              </Text>
              <InputNumber
                style={{ width: '100%' }}
                min={0} max={100}
                value={buScoreBenchmark}
                onChange={(value) => setBuScoreBenchmark(value || 0)}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
              />
            </Col>
            <Col span={4}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>&nbsp;</Text>
              <Button
                type="primary"
                onClick={generateBuReport}
                loading={buReportLoading}
                disabled={!buSelectedOrganization || !buSelectedMonth || buCampaigns.length === 0}
              >
                Generate Report
              </Button>
            </Col>
          </Row>

          {/* Campaign info */}
          {buSelectedOrganization && buSelectedMonth && (
            <div>
              {campaignsLoading ? (
                <Text type="secondary">Loading campaigns…</Text>
              ) : buCampaigns.length === 0 ? (
                <Alert
                  message="No campaigns found"
                  description={`No campaigns targeted ${buAllOrganizations.find(o => o.id === buSelectedOrganization)?.name} during ${buSelectedMonth?.format('MMMM YYYY')}`}
                  type="warning"
                  showIcon
                />
              ) : (
                <Alert
                  message={`Found ${buCampaigns.length} campaign(s) — click Generate Report to continue`}
                  description={
                    <div>
                      {buCampaigns.slice(0, 5).map(c => (
                        <Tag key={c._id} color="blue" style={{ marginBottom: 4 }}>{c.name}</Tag>
                      ))}
                      {buCampaigns.length > 5 && <Tag>+{buCampaigns.length - 5} more</Tag>}
                    </div>
                  }
                  type="info"
                  showIcon
                />
              )}
            </div>
          )}
        </Space>
      )}

      {/* ── Step 1: Report Preview ── */}
      {buWizardStep === 1 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button
              onClick={() => setBuWizardStep(0)}
              style={{ marginRight: 8 }}
            >
              ← Back to Settings
            </Button>
          </div>
          {buReportLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 12, color: '#6b7280' }}>Generating report…</div>
            </div>
          ) : buReportData ? (
            <BusinessUnitReport
              data={buReportData}
              defaultReportName={buReportName}
              benchmark={buCompletionRateBenchmark || buScoreBenchmark || 90}
            />
          ) : null}
        </>
      )}
    </Card>
  );

  const renderMultiCampaignInterface = () => {
    const effectiveNetworkName = mcPdfOptions.networkName || mcSelectedGroupOwner || (mcSelectedManagingGroup.length === 1 ? mcSelectedManagingGroup[0] : '');

    const openInNewTab = () => {
      sessionStorage.setItem('mcReportData', JSON.stringify(reportData));
      sessionStorage.setItem('mcReportName', reportName || 'Multi-Campaign Report');
      sessionStorage.setItem('mcReportCustomisation', JSON.stringify({
        networkName: effectiveNetworkName,
        reportLabel: mcPdfOptions.reportLabel,
        orgLabel: mcPdfOptions.orgLabel,
        orgLabelPlural: mcPdfOptions.orgLabelPlural,
        deptLabel: mcPdfOptions.deptLabel,
        deptLabelPlural: mcPdfOptions.deptLabelPlural,
      }));
      sessionStorage.removeItem('mcSavedReportId');
      sessionStorage.removeItem('mcSavedAiInsights');
      window.open('/report-view', '_blank');
    };

    const lastConfig = (() => { try { return JSON.parse(localStorage.getItem('mcLastConfig')); } catch { return null; } })();
    const dataLoaded = !!reportData?._rawData;
    const reportReady = !!reportData?.departmentGroupConfig;

    const deptCount = {};
    editableGroups.forEach(g => (g.departments || []).forEach(d => { deptCount[d] = (deptCount[d] || 0) + 1; }));
    const unassignedCount = availableDepartments.filter(d => !deptCount[d]).length;
    const duplicatedCount = availableDepartments.filter(d => deptCount[d] > 1).length;

    return (
      <>
        {/* ── "Using last config" banner ── */}
        {mcLoadedFromReport && (
          <div style={{
            background: '#e6f4ff', border: '1px solid #91caff', borderRadius: 8,
            padding: '10px 16px', marginBottom: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Text style={{ fontSize: 13 }}>
              <strong>Loaded from:</strong> {mcLoadedFromReport.title}
              {' — select campaigns for the new month and click Load Campaigns.'}
            </Text>
            <Button size="small" onClick={() => setMcLoadedFromReport(null)}>Dismiss</Button>
          </div>
        )}

        {/* ── Quick-generate strip ── */}
        {lastConfig && !reportReady && !mcLoadedFromReport && (
          <div style={{
            background: 'linear-gradient(135deg, #1a3a6b 0%, #267a96 100%)',
            borderRadius: 8, padding: '12px 20px', marginBottom: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Last report</div>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                {lastConfig.reportName}{lastConfig.month ? ' · ' + lastConfig.month : ''}
              </div>
            </div>
            <Button
              size="small"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
              onClick={() => {
                if (lastConfig.managingGroups) setMcSelectedManagingGroup(lastConfig.managingGroups);
                if (lastConfig.groupOwner) setMcSelectedGroupOwner(lastConfig.groupOwner);
                if (lastConfig.month) setMcSelectedMonth(dayjs(lastConfig.month, 'MMMM YYYY'));
                if (lastConfig.reportName) setReportName(lastConfig.reportName);
                if (lastConfig.pdfOptions) setMcPdfOptions(prev => ({ ...prev, ...lastConfig.pdfOptions }));
              }}
            >
              Restore settings
            </Button>
          </div>
        )}

        <Card
          title="Multi-Campaign Report"
          extra={
            <Button onClick={() => {
              setSelectedReportType(null);
              setMcWizardStep(0);
              setReportData(null);
              setMcLoadedFromReport(null);
            }}>
              Back to Reports
            </Button>
          }
        >
          <Row gutter={24} align="top">

            {/* ── Left column: config ── */}
            <Col xs={24} lg={16}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>

                {/* Source & Scope */}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>Source & Scope</Text>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Report Name</Text>
                      <Input
                        placeholder="Enter report name"
                        value={reportName}
                        onChange={e => setReportName(e.target.value)}
                      />
                    </div>
                    <Row gutter={12}>
                      <Col span={8}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Group Owner</Text>
                        <Select
                          placeholder="Filter by owner..."
                          style={{ width: '100%' }}
                          value={mcSelectedGroupOwner}
                          onChange={(value) => {
                            setMcSelectedGroupOwner(value);
                            if (value) {
                              const owner = mcAllGroupOwners.find(o => o.name === value);
                              if (owner?.managingGroups?.length) setMcSelectedManagingGroup(owner.managingGroups);
                            } else {
                              setMcSelectedManagingGroup([]);
                            }
                            setSelectedCampaignIds([]);
                            setReportData(null);
                          }}
                          options={mcAllGroupOwners.map(o => ({ label: o.name, value: o.name }))}
                          allowClear
                        />
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Managing Groups</Text>
                        <Select
                          mode="multiple"
                          placeholder="Select group(s)..."
                          style={{ width: '100%' }}
                          value={mcSelectedManagingGroup}
                          onChange={(value) => { setMcSelectedManagingGroup(value); setSelectedCampaignIds([]); setReportData(null); }}
                          options={(
                            mcSelectedGroupOwner
                              ? (mcAllGroupOwners.find(o => o.name === mcSelectedGroupOwner)?.managingGroups || [])
                              : mcAllManagingGroups
                          ).map(g => ({ label: g, value: g }))}
                          allowClear
                          maxTagCount="responsive"
                        />
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Month</Text>
                        <DatePicker
                          picker="month"
                          style={{ width: '100%' }}
                          value={mcSelectedMonth}
                          onChange={(value) => { setMcSelectedMonth(value); setSelectedCampaignIds([]); setReportData(null); }}
                          placeholder="Select month"
                        />
                      </Col>
                    </Row>
                    {mcSelectedManagingGroup.length > 0 && mcSelectedMonth && (
                      <div>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Campaigns</Text>
                        <Select
                          mode="multiple"
                          placeholder="Choose campaigns..."
                          style={{ width: '100%' }}
                          value={selectedCampaignIds}
                          onChange={v => { setSelectedCampaignIds(v); setReportData(null); }}
                          options={campaignOptions}
                          loading={campaignsLoading}
                          maxTagCount="responsive"
                          showSearch
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          optionRender={(option) => (
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                              <span>{option.data.label}</span>
                              <Space>
                                <Tag color="blue">{option.data.organization}</Tag>
                                <Tag color={option.data.statusColor}>{option.data.status}</Tag>
                              </Space>
                            </Space>
                          )}
                        />
                        {campaignsLoading && <Text type="secondary" style={{ fontSize: 12 }}>Loading campaigns...</Text>}
                        {!campaignsLoading && campaigns.length === 0 && (
                          <Alert
                            message="No campaigns found"
                            description={`No campaigns for ${mcSelectedManagingGroup.join(', ')} in ${mcSelectedMonth?.format('MMMM YYYY')}`}
                            type="warning" showIcon style={{ marginTop: 8 }}
                          />
                        )}
                      </div>
                    )}
                  </Space>
                </div>

                {/* Department Groups */}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 13 }}>Department Groups</Text>
                    {dataLoaded && (
                      <Space size={6}>
                        <Select
                          placeholder="Load preset..."
                          style={{ width: 160 }}
                          allowClear
                          size="small"
                          options={availableDeptPresets.map(p => ({ label: p.name, value: p.name }))}
                          onChange={(val) => { if (val) handleLoadPreset(val); else setLoadedPresetName(null); }}
                          value={loadedPresetName}
                        />
                        {loadedPresetName ? (
                          <>
                            <Button size="small" type="primary" onClick={handleUpdatePreset}>Update</Button>
                            <Popconfirm title={`Delete "${loadedPresetName}"?`} onConfirm={handleDeletePreset} okText="Delete" okType="danger" cancelText="Cancel">
                              <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </>
                        ) : (
                          <Button size="small" onClick={() => { setSavePresetName(''); setSavePresetModalOpen(true); }}>Save preset</Button>
                        )}
                        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCustomGroupModalOpen(true)}>Add Group</Button>
                      </Space>
                    )}
                  </div>

                  {!dataLoaded ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#bbb', fontSize: 13 }}>
                      Select campaigns above and click <strong>Load Campaigns</strong> to configure department groups
                    </div>
                  ) : (
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text style={{ fontSize: 12 }}>{availableDepartments.length} departments detected</Text>
                          <div style={{ fontSize: 11, display: 'flex', gap: 12 }}>
                            {unassignedCount > 0 && <span style={{ color: '#ff4d4f' }}>&#10007; {unassignedCount} unassigned</span>}
                            {duplicatedCount > 0 && <span style={{ color: '#fa8c16' }}>&#9888; {duplicatedCount} duplicated</span>}
                            {unassignedCount === 0 && duplicatedCount === 0 && availableDepartments.length > 0 && <span style={{ color: '#52c41a' }}>&#10003; All covered</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {availableDepartments.map(dept => {
                            const count = deptCount[dept] || 0;
                            if (count === 0) return <Tag key={dept} style={{ color: '#cf1322', background: '#fff2f0', borderColor: '#ffa39e', fontSize: 11, margin: 0 }}>&#10007; {dept}</Tag>;
                            if (count > 1) return <Tag key={dept} style={{ color: '#d46b08', background: '#fff7e6', borderColor: '#ffd591', fontSize: 11, margin: 0 }}>&#9888; {dept}</Tag>;
                            return <Tag key={dept} style={{ color: '#389e0d', background: '#f6ffed', borderColor: '#b7eb8f', fontSize: 11, margin: 0 }}>&#10003; {dept}</Tag>;
                          })}
                        </div>
                      </div>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {editableGroups.map(group => (
                          <Card key={group.id} size="small" style={{ backgroundColor: group.isCustom ? '#e6f7ff' : '#fafafa' }}>
                            <Row justify="space-between" align="top">
                              <Col span={20}>
                                <Text strong style={{ fontSize: 13 }}>{group.label || group.name}</Text>
                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{group.departments?.length || 0} depts</Text>
                                <Row gutter={[8, 6]} style={{ marginTop: 8 }}>
                                  {availableDepartments.map(dept => {
                                    const isChecked = group.departments?.includes(dept) || false;
                                    const isDuplicate = isChecked && editableGroups.some(g => g.id !== group.id && g.departments?.includes(dept));
                                    return (
                                      <Col key={dept} span={8}>
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={(e) => {
                                            const newDepts = e.target.checked
                                              ? [...(group.departments || []), dept]
                                              : (group.departments || []).filter(d => d !== dept);
                                            handleUpdateGroupDepartments(group.id, newDepts);
                                          }}
                                        >
                                          <span style={{ fontSize: 12, ...(isDuplicate ? { color: '#fa8c16', fontWeight: 600 } : {}) }}>
                                            {dept}{isDuplicate ? ' !' : ''}
                                          </span>
                                        </Checkbox>
                                      </Col>
                                    );
                                  })}
                                </Row>
                              </Col>
                              <Col span={4} style={{ textAlign: 'right' }}>
                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveGroup(group.id)} />
                              </Col>
                            </Row>
                          </Card>
                        ))}
                      </Space>
                    </Space>
                  )}
                </div>

                {/* Advanced: Labels (collapsible) */}
                <details style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <summary style={{ padding: '12px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600, userSelect: 'none' }}>
                    Advanced &middot; Labels
                  </summary>
                  <div style={{ padding: '4px 20px 16px' }}>
                    <Row gutter={[12, 12]}>
                      <Col span={6}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Organisation (singular)</Text>
                        <Input size="small" placeholder="Dealership" value={mcPdfOptions.orgLabel} onChange={e => setMcPdfOptions(prev => ({ ...prev, orgLabel: e.target.value }))} />
                      </Col>
                      <Col span={6}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Organisation (plural)</Text>
                        <Input size="small" placeholder="Dealerships" value={mcPdfOptions.orgLabelPlural} onChange={e => setMcPdfOptions(prev => ({ ...prev, orgLabelPlural: e.target.value }))} />
                      </Col>
                      <Col span={6}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Role/Dept (singular)</Text>
                        <Input size="small" placeholder="Department" value={mcPdfOptions.deptLabel} onChange={e => setMcPdfOptions(prev => ({ ...prev, deptLabel: e.target.value }))} />
                      </Col>
                      <Col span={6}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Role/Dept (plural)</Text>
                        <Input size="small" placeholder="Departments" value={mcPdfOptions.deptLabelPlural} onChange={e => setMcPdfOptions(prev => ({ ...prev, deptLabelPlural: e.target.value }))} />
                      </Col>
                    </Row>
                  </div>
                </details>

              </Space>
            </Col>

            {/* ── Right rail ── */}
            <Col xs={24} lg={8}>
              <div style={{ position: 'sticky', top: 24 }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>

                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}>
                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Generate</Text>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
                      {!selectedCampaignIds.length
                        ? 'Select campaigns to get started'
                        : !dataLoaded
                        ? 'Click below to load campaign data'
                        : reportReady
                        ? 'Report ready — regenerate or open'
                        : 'Dept groups configured — ready to generate'}
                    </Text>
                    {!dataLoaded ? (
                      <Button
                        type="primary" block size="large"
                        disabled={!selectedCampaignIds.length}
                        loading={backendReportLoading}
                        onClick={handleGenerateReportFromBackend}
                      >
                        Load Campaigns
                      </Button>
                    ) : (
                      <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <Button
                          type="primary" block size="large"
                          loading={reportLoading}
                          onClick={handleGenerateFinalReport}
                        >
                          {reportReady ? 'Regenerate Report' : 'Generate Report'}
                        </Button>
                        {reportReady && (
                          <Button block onClick={openInNewTab}>
                            Open in New Tab
                          </Button>
                        )}
                        <Button
                          block size="small"
                          loading={backendReportLoading}
                          onClick={handleGenerateReportFromBackend}
                        >
                          Reload campaign data
                        </Button>
                      </Space>
                    )}
                  </div>

                  {dataLoaded && (
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px', background: '#fafafa' }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {[
                          ['Campaigns', selectedCampaignIds.length],
                          ['Dept groups', editableGroups.length],
                          ['Month', mcSelectedMonth?.format('MMM YYYY') || '—'],
                          ['Managing groups', mcSelectedManagingGroup.length || '—'],
                        ].map(([label, val]) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <Text type="secondary">{label}</Text>
                            <Text>{val}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>
                  )}

                </Space>
              </div>
            </Col>

          </Row>
        </Card>

        {/* ── Inline Report Preview ── */}
        {reportReady && (
          <div id="mc-inline-report" style={{ marginTop: 24 }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #1A3A6B 0%, #267a96 100%)',
              borderRadius: '8px 8px 0 0', padding: '12px 20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 1.5 }}>TapLearnGo</div>
                <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{reportName || 'Multi-Campaign Report'}</div>
              </div>
              <button
                onClick={openInNewTab}
                style={{
                  background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Open in New Tab
              </button>
            </div>
            <div style={{ background: '#F0F4F8', borderRadius: '0 0 8px 8px', padding: '24px 16px' }}>
              <MultiCampaignReport
                aggregates={reportData}
                loading={false}
                formatPercentDisplay={formatPercentDisplay}
                formatNumberDisplay={formatNumberDisplay}
                percentValue={percentValue}
                reportName={reportName}
                savedReportId={null}
                initialAiInsights={null}
                networkName={effectiveNetworkName}
                reportLabel={mcPdfOptions.reportLabel}
                orgLabel={mcPdfOptions.orgLabel}
                orgLabelPlural={mcPdfOptions.orgLabelPlural}
                deptLabel={mcPdfOptions.deptLabel}
                deptLabelPlural={mcPdfOptions.deptLabelPlural}
              />
            </div>
          </div>
        )}
        {/* Sub-modals (always mounted) */}
        <Modal
          title="Add Custom Department Group"
          open={customGroupModalOpen}
          onOk={handleAddCustomGroup}
          onCancel={() => { setCustomGroupModalOpen(false); setCustomGroupName(''); setCustomGroupDepts([]); }}
          okText="Create Group" cancelText="Cancel"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Group Name</Text>
              <Input placeholder="e.g., IT Team" value={customGroupName} onChange={(e) => setCustomGroupName(e.target.value)} />
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Select Departments</Text>
              <Row gutter={[8, 8]}>
                {availableDepartments.map(dept => (
                  <Col key={dept} span={12}>
                    <Checkbox
                      checked={customGroupDepts.includes(dept)}
                      onChange={(e) => {
                        if (e.target.checked) setCustomGroupDepts([...customGroupDepts, dept]);
                        else setCustomGroupDepts(customGroupDepts.filter(d => d !== dept));
                      }}
                    >{dept}</Checkbox>
                  </Col>
                ))}
              </Row>
            </div>
          </Space>
        </Modal>
        <Modal
          title="Save Current Groups as Preset"
          open={savePresetModalOpen}
          onOk={handleSaveAsPreset}
          onCancel={() => { setSavePresetModalOpen(false); setSavePresetName(''); }}
          okType="default" okText="Save"
        >
          <Input
            placeholder="Preset name (e.g. MBSA Standard)"
            value={savePresetName}
            onChange={e => setSavePresetName(e.target.value)}
          />
        </Modal>
      </>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="Reports" 
        subtitle="Explore analytics by campaign, organization, or department"
        breadcrumbs={["Dashboard", "Reports"]}
      />

      <div style={{ marginTop: 16 }}>
        {!selectedReportType ? renderSavedReports() : null}
        {!selectedReportType ? renderReportCards() : null}
        {selectedReportType === 'multi-campaign' && (
          <NewReportPage
            store={{
              reportName, setReportName,
              month: mcSelectedMonth, setMonth: setMcSelectedMonth,
              managingGroups: mcSelectedManagingGroup, setManagingGroups: setMcSelectedManagingGroup,
              allManagingGroups: mcAllManagingGroups,
              groupOwner: mcSelectedGroupOwner, setGroupOwner: setMcSelectedGroupOwner,
              allGroupOwners: mcAllGroupOwners,
              campaigns, campaignsLoading, campaignOptions,
              selectedCampaignIds, setSelectedCampaignIds,
              reportData, setReportData,
              reportLoading, backendReportLoading,
              editableGroups, setEditableGroups,
              availableDepartments,
              pdfOptions: mcPdfOptions, setPdfOptions: setMcPdfOptions,
              availableDeptPresets,
              loadedPresetName, setLoadedPresetName,
              loadedFromReport: mcLoadedFromReport, setLoadedFromReport: setMcLoadedFromReport,
              customGroupModalOpen, setCustomGroupModalOpen,
              customGroupName, setCustomGroupName,
              customGroupDepts, setCustomGroupDepts,
              savePresetModalOpen, setSavePresetModalOpen,
              savePresetName, setSavePresetName,
              onLoadCampaigns: handleGenerateReportFromBackend,
              onGenerateReport: handleGenerateFinalReport,
              onLoadPreset: handleLoadPreset,
              onUpdatePreset: handleUpdatePreset,
              onDeletePreset: handleDeletePreset,
              onSaveAsPreset: handleSaveAsPreset,
              onAddCustomGroup: handleAddCustomGroup,
              onRemoveGroup: handleRemoveGroup,
              onUpdateGroupDepartments: handleUpdateGroupDepartments,
              onSaveReport: handleSaveReport,
              saving: mcSaving,
              pdfProfiles: mcPdfProfiles,
              formatPercentDisplay,
              formatNumberDisplay,
              percentValue,
            }}
            onBack={() => {
              setSelectedReportType(null);
              setMcWizardStep(0);
              setReportData(null);
              setMcLoadedFromReport(null);
            }}
            onReportSaved={loadSavedReports}
          />
        )}
        {selectedReportType === 'business-unit' && (
          <NewBUReportPage
            store={{
              reportName: buReportName, setReportName: setBuReportName,
              selectedOrganization: buSelectedOrganization, setSelectedOrganization: setBuSelectedOrganization,
              allOrganizations: buAllOrganizations,
              selectedMonth: buSelectedMonth, setSelectedMonth: setBuSelectedMonth,
              campaigns: buCampaigns, campaignsLoading,
              selectedCampaignIds: buSelectedCampaignIds, setSelectedCampaignIds: setBuSelectedCampaignIds,
              reportData: buReportData, setReportData: setBuReportData,
              reportLoading: buReportLoading,
              completionBenchmark: buCompletionRateBenchmark, setCompletionBenchmark: setBuCompletionRateBenchmark,
              scoreBenchmark: buScoreBenchmark, setScoreBenchmark: setBuScoreBenchmark,
              onGenerate: generateBuReport,
            }}
            onBack={() => {
              setSelectedReportType(null);
              setBuSelectedOrganization(null);
              setBuSelectedMonth(null);
              setBuCampaigns([]);
              setBuReportData(null);
              setBuWizardStep(0);
            }}
          />
        )}
        {selectedReportType === 'department' && (
          <Card title="Department Report" extra={<Button onClick={() => setSelectedReportType(null)}>Back to Reports</Button>}>
            <Text>Department report interface coming soon...</Text>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Reports;
